// src/components/Trades.tsx

import React, { useState, useEffect, useMemo, useCallback, useContext, useRef, PropsWithChildren } from 'react';
import {
    collection,
    getDocs,
    query,
    orderBy,
    doc,
    where,
    updateDoc,
    onSnapshot,
    writeBatch,
    QueryFieldFilterConstraint,
    QueryOrderByConstraint,
    limit,
} from 'firebase/firestore';
import { DatePickerInput } from '@mantine/dates';
import { fromMantineDate } from '../../helpers/mantineDates';
import {
    Select,
    Group,
    Loader,
    Text,
    Badge,
    Center,
    NumberInput,
    ActionIcon,
    SimpleGrid,
    Box,
    Tooltip,
    Grid,
    Menu,
} from '@mantine/core';
import { IconX, IconAlertTriangle, IconCheck, IconTable, IconLock, IconTrash, IconPlus, IconArrowsLeftRight, IconUpload } from '@tabler/icons-react';
import { format, parse } from 'date-fns';
import { MantineReactTable, useMantineReactTable, type MRT_ColumnDef, type MRT_GroupingState, type MRT_Row, type MRT_Cell, type MRT_TableInstance } from 'mantine-react-table';
import 'mantine-react-table/styles.css';
import { useFirestore } from '../../helpers/firebaseContext';
import { CombinedPortfolioGroup, CustomClaims, Portfolio, PortfolioWarning, TradeAllocation, TradeDate, QTY_EPSILON, isAllfundsTradeAllocatable, qtyEq, stripNoise } from '../../types';
import { Trade } from '../../types';
import { ConfigContext, MyUserDataContext } from '../../contexts';
import { PortfolioAllocationStatsModal } from './portfolioAllocationStatsModal';
import { WarningsModal } from './warningsModal';
import ExcelUploadComponent from './excel_upload_component';
import { notifications } from '@mantine/notifications';
import PortfolioGroupsManager from './portfolio_groups_manager';
import TradeAllocationExcel from './trade_allocation_excel';
import ManualTradesTable from './manual_trades_table';
import { useUndoRedo } from '../../hooks/useUndoRedo';
import { UndoRedoControls } from './UndoRedoControls';
import { useActionBasedUndoRedo, AllocationAction } from '../../hooks/useActionBasedUndoRedo';
import { useConfirmation } from '../../components/ConfirmationModal';
import { getAllowedPortfolioTypeCodes, getEffectivePortfolioType } from './trades_permissions';
import { GlobalDpmAllocationRowsPanel, GlobalDpmAllocationUploadModal, GlobalDpmDateCards, GlobalDpmUploadsModal } from './global_dpm_allocations';


// Utility function to create portfolio display names
const createPortfolioDisplayName = (portfolio: Portfolio, style: 'first-word' | 'abbreviation' = 'abbreviation'): string => {
    if (!portfolio.code) return portfolio.id || 'Unknown Portfolio';

    if (style === 'first-word') {
        const firstName = portfolio.name_en?.split(" ")[0] || '';
        return `${portfolio.code} - ${firstName}`;
    }

    // Create abbreviation from all words, filtering out stopwords
    const stopwords = new Set(["the", "for", "of", "and", "in", "on", "at", "to", "by", "with", "a", "an"]);
    const nameAbbr = portfolio.name_en
        ? portfolio.name_en
            .split(" ")
            .filter(word => word && !stopwords.has(word.toLowerCase()))
            .map(word => word[0])
            .join("")
            .toUpperCase()
        : "";

    return `${portfolio.code} - ${nameAbbr}`;
};



export function NoShellPadding({ children }: PropsWithChildren) {
    return (
        <Box style={{ '--app-shell-padding': '0px' } as React.CSSProperties}>
            {children}
        </Box>
    );
}

// Pre-created memoized badges for BUY and SELL
const BUY_BADGE = React.memo(() => <Badge color="green">BUY</Badge>);
const SELL_BADGE = React.memo(() => <Badge color="red">SELL</Badge>);
const FULLY_ALLOCATED_STYLE = {
    style: {
        backgroundColor: 'rgba(0, 180, 0, 0.1)',
    }
};

const MIN_PORTFOLIO_VALUE_KD = 1; // Minimum portfolio value to show in the table
const UNDO_WAIT_MS = 5000;


// Returns the cash cost/value of a trade in the same unit as the portfolio's cashMap[currency].
// KWD trades are priced in fils (1 KWD = 1000 fils) so we convert to KWD.
// Trades in other currencies are already priced in their native currency.
const getTradeCashAmount = (currency: string | undefined, price: number, quantity: number): number => {
    const isKuwaitStock = !currency || currency.toUpperCase() === 'KWD';
    return isKuwaitStock ? (price * quantity) / 1000 : price * quantity;
};

// Helper function to calculate allocation totals
const calculateAllocationTotals = (row: MRT_Row<Trade>, allocations: Record<string, { portfolioId: string; qty: number }[]>) => {
    if (row.getIsGrouped()) {
        return calculateGroupedRowTotals(row, allocations);
    } else {
        return calculateSingleRowTotals(row, allocations);
    }
};

const calculateGroupedRowTotals = (row: MRT_Row<Trade>, allocations: Record<string, { portfolioId: string; qty: number }[]>) => {
    let totalAllocated = 0;
    let totalFilledQty = 0;

    const leafRows = row.getLeafRows ? row.getLeafRows() : [];
    leafRows.forEach(leafRow => {
        // Check if the leaf row has an original trade with an ID and is not a group header
        // if (leafRow.original?.id && leafRow.original.id.toString() === leafRow.id) {
        if (!leafRow.getIsGrouped() && leafRow.original?.id) {
            totalFilledQty += leafRow.original.filledQty || 0;
            const rowAllocations = allocations[leafRow.original.id] || [];
            totalAllocated += rowAllocations.reduce((sum, a) => sum + a.qty, 0);
        }
    });

    return { totalAllocated, totalFilledQty };
};

const calculateSingleRowTotals = (row: MRT_Row<Trade>, allocations: Record<string, { portfolioId: string; qty: number }[]>) => {
    const trade = row.original;
    if (!trade) {
        return { totalAllocated: 0, totalFilledQty: 0 };
    }

    const tradeAllocations = allocations[trade.id] || [];
    const totalAllocated = tradeAllocations.reduce((sum, a) => sum + a.qty, 0);
    const totalFilledQty = trade.filledQty || 0;

    return { totalAllocated, totalFilledQty };
};

// Helper function to determine if row is fully allocated
const isFullyAllocated = (totalAllocated: number, totalFilledQty: number) => {
    // epsilon compare — fund units (GLOBAL_DPM) are fractional
    return qtyEq(totalAllocated, totalFilledQty) && totalFilledQty > 0;
};

// Full-precision qty display: strips float noise, never truncates fund-unit fractions
// (default toLocaleString caps at 3 fraction digits).
const fmtQty = (n: number | undefined | null): string =>
    stripNoise(n ?? 0).toLocaleString(undefined, { maximumFractionDigits: 8 });

// Sentinel value in the asset-manager dropdown that filters to Global DPM portfolios
// (any portfolio carrying a portfolioContract) instead of a manager name.
const GLOBAL_DPM_MANAGER_FILTER = '__GLOBAL_DPM__';
const matchesAssetManagerFilter = (portfolio: Portfolio | undefined, selected: string): boolean =>
    selected === '' ? true
        : selected === GLOBAL_DPM_MANAGER_FILTER ? !!portfolio?.portfolioContract
            : portfolio?.managerName === selected;

// Utility function to check if allocations are identical (ignoring commission/stamps)
const areAllocationsIdentical = (oldAllocs: TradeAllocation[], newAllocs: TradeAllocation[]): boolean => {
    if (oldAllocs.length !== newAllocs.length) return false;

    // Sort both arrays by portfolioId for consistent comparison
    const sortedOld = [...oldAllocs].sort((a, b) => a.portfolioId.localeCompare(b.portfolioId));
    const sortedNew = [...newAllocs].sort((a, b) => a.portfolioId.localeCompare(b.portfolioId));

    for (let i = 0; i < sortedOld.length; i++) {
        if (sortedOld[i].portfolioId !== sortedNew[i].portfolioId ||
            sortedOld[i].qty !== sortedNew[i].qty) {
            return false;
        }
    }

    return true;
};

// Utility function to check if trade has meaningful changes (excluding commission/stamps)
const hasSignificantTradeChanges = (oldTrade: Trade, newTrade: Trade, currentAllocations?: { portfolioId: string; qty: number }[]): boolean => {
    // List of fields to ignore
    const ignoredFields: string[] = [];

    // Check main trade fields (excluding ignored ones)
    for (const key in newTrade) {
        if (ignoredFields.includes(key)) continue;

        if (key === 'allocations') {
            // Use the current allocations from our state if provided, otherwise use what's in oldTrade
            const oldAllocs = currentAllocations || oldTrade.allocations || [];
            const newAllocs = newTrade.allocations || [];

            if (!areAllocationsIdentical(oldAllocs, newAllocs)) {
                return true;
            }
        } else if (key === 'tradeDate' || key === 'createdAt') {
            // Compare timestamps
            if (oldTrade[key]?.seconds !== newTrade[key]?.seconds) return true;
        } else {
            // Compare other fields
            if (oldTrade[key as keyof Trade] !== newTrade[key as keyof Trade]) return true;
        }
    }

    return false;
};
// Create a simple caching mechanism with static pre-rendered components
// This approach is focused on pure rendering performance without complex factory patterns
let symbolComponentCache: Record<string, React.ReactNode> = {};

// A component that acts as a cache layer for symbols
const CachedSymbol = React.memo(({ symbolCode, info }: {
    symbolCode: string;
    info: { ticker: string; name: string }
}) => {
    // Use this one component instance to manage all symbol renderings
    // Only new symbol codes will trigger actual renders of new content

    // Check if this symbol has already been cached
    if (!symbolComponentCache[symbolCode]) {
        // console.log(`CACHE MISS: Creating new symbol component for ${info.ticker} (${symbolCode})`);

        // Create and cache the content for this symbol
        symbolComponentCache[symbolCode] = (
            <Box>
                <Text fw={500}>{info.ticker}</Text>
                {/* <Text size="xs" c="dimmed">{info.name}</Text> */}
            </Box>
        );
    } else {
        // console.log(`CACHE HIT: Reusing symbol component for ${info.ticker}`);
    }

    // Return the cached content
    return <>{symbolComponentCache[symbolCode]}</>;
});

// Function to track cache stats
const getSymbolCacheStats = () => {
    console.log(`Symbol cache has ${Object.keys(symbolComponentCache).length} unique symbols cached`);
    return Object.keys(symbolComponentCache).join(', ');
};

// Custom aggregation function with correct signature
const showValueIfSame = <TData extends Record<string, any>>(
    columnId: string, // Parameter required by MRT, though not used directly here
    leafRows: MRT_Row<TData>[],
): any | null => {
    if (!leafRows || leafRows.length === 0) return null;
    // Get the first value using the columnId
    const firstValue = leafRows[0]?.original?.[columnId];
    if (firstValue === undefined) return null; // Handle case where value might not exist

    // Check if all other rows have the same value
    const allSame = leafRows.every(row => row?.original?.[columnId] === firstValue);

    return allSame ? firstValue : null;
};

// Performance measurement utility
const perf = {
    start: (label: string) => {
        console.time(`⏱️ ${label}`);
        return () => console.timeEnd(`⏱️ ${label}`);
    },
    log: (label: string, data?: any) => {
        console.log(`📊 ${label}`, data);
    }
};

export type EmbellishedTrade = Trade & {
    formattedTime?: string; // Add formatted time field
    formattedPrice?: string; // Add formatted price
    formattedQty?: string; // Add formatted quantity
    formattedUnallocQty?: string; // Formatted unallocated quantity
};
type SecurityInfo = { ticker: string; name: string };

// Types for running balances
interface SecurityBalance {
    id: string;
    quantity: number;
    name: string;
}

interface RunningBalance {
    portfolioId: string;
    // cash: number;
    cashMap?: Record<string, number>; // Map of currency code (e.g. KWD, AED, USD) to cash amount.
    // Cash is shared across exchanges that trade in the same currency
    // (e.g. ADX and DFM both use AED, so they draw from the same AED bucket).
    securities: Record<string, SecurityBalance>;
}

// Normalize an optional currency value to a non-empty uppercase code.
// Trades and positions sometimes omit the currency for KWD, so we default to KWD.
const resolveCurrency = (currency: string | undefined | null): string => {
    return (currency && currency.trim().length > 0 ? currency : 'KWD').toUpperCase();
};

// Function to initialize running balances from portfolio data (moved outside the component)
const initializeRunningBalances = (portfolios: Portfolio[]) => {
    const balances: Record<string, RunningBalance> = {};

    portfolios.forEach(portfolio => {
        if (!portfolio.id) return;

        const securities: Record<string, SecurityBalance> = {};
        const cashMap: Record<string, number> = {};

        // Initialize securities from local_position
        portfolio.position?.local_position?.forEach(position => {
            if (position.firebase_key && position.code && position.code !== 'KWD' && !position.cash) {
                securities[position.firebase_key] = {
                    id: position.firebase_key,
                    quantity: position.quantity || 0,
                    name: position.name || ''
                };
            } else if (position.code === 'KWD' && position.cash) {
                const currencyKey = resolveCurrency(position.currency || position.code);
                cashMap[currencyKey] = (cashMap[currencyKey] || 0) + (position.quantity || 0);
            }
        });

        // Add foreign positions if needed
        portfolio.position?.foreign_position?.forEach(position => {
            if (position.firebase_key && !position.cash) {
                securities[position.firebase_key] = {
                    id: position.firebase_key,
                    quantity: position.quantity || 0,
                    name: position.name || ''
                };
            } else if (position.cash && (position.currency || position.code)) {
                // Cash is bucketed by currency, so e.g. AED cash from ADX and DFM
                // share the same cashMap['AED'] entry.
                const currencyKey = resolveCurrency(position.currency || position.code);
                cashMap[currencyKey] = (cashMap[currencyKey] || 0) + (position.quantity || 0);
            }
        });

        balances[portfolio.id] = {
            portfolioId: portfolio.id,
            cashMap,
            securities
        };
    });

    console.log(`Initialized running balances for ${Object.keys(balances).length} portfolios`);

    return balances;
};

// Define the props for the new component
interface ActionCellProps {
    trade: Trade;
    allocations: { portfolioId: string; qty: number }[];
    partialInput: { portfolioId: string; qty: number };
    portfolioOptions: { value: string; label: string }[];
    onPortfolioChange: (id: string, v: string) => void;
    onQtyChange: (id: string, v: number) => void;
    handleAssign: (trade: Trade & { customInput?: { portfolioId: string; qty: number } }, onLoadingChange: (isLoading: boolean) => void) => void;
    isAllocationValid: (portfolioId: string, symbolID: string, side: 'BUY' | 'SELL', quantity: number, price: number, exchange: string, currency: string | undefined, specificBalances?: Record<string, RunningBalance>) => { valid: boolean; message: string };
    portfolioWarnings: Record<string, PortfolioWarning[]>;
    runningBalances: Record<string, RunningBalance>;
}

const ActionCell = React.memo(
    ({
        trade,
        allocations,
        partialInput,
        portfolioOptions,
        onPortfolioChange,
        onQtyChange,
        handleAssign,
        isAllocationValid,
        portfolioWarnings,
        runningBalances,
    }: ActionCellProps) => {
        // Memoize the remaining quantity calculation to avoid recalculating on every render
        const remaining = useMemo(() =>
            getRemainingQty(trade, allocations),
            [trade.id, trade.filledQty, allocations]);

        // Use local state instead of relying on parent state
        const [localPortfolioId, setLocalPortfolioId] = useState(() => partialInput.portfolioId || '');
        const [localQty, setLocalQty] = useState(() => partialInput.qty || remaining);
        // GLOBAL_DPM (AllFunds) rows: fund units are fractional and the team thinks in
        // amounts, so the qty input defaults to amount mode (units = amount ÷ NAV).
        const isGlobal = trade.accountId === 'GLOBAL_DPM';
        const allocatable = isAllfundsTradeAllocatable(trade);
        // Fractional units are opt-in per exchange via config; everything else is integer-only.
        const configContext = useContext(ConfigContext);
        const isFractional = (configContext.tradesMap?.fractionalExchanges ?? []).includes(trade.exchange);
        const [amountMode, setAmountMode] = useState(isGlobal);
        // The amount the team types/sees is what the customer is actually charged:
        // invested amount plus (BUY) or minus (SELL) their pro-rata share of the fee.
        const feePerUnit = trade.totalCommission && trade.filledQty ? trade.totalCommission / trade.filledQty : 0;
        const grossPerUnit = (trade.price || 0) + (trade.side === 'BUY' ? feePerUnit : -feePerUnit);
        const [localAmount, setLocalAmount] = useState(() => stripNoise((partialInput.qty || remaining) * grossPerUnit));
        // Local loading state
        const [isLoading, setIsLoading] = useState(false);
        // Key to reset Select when options change
        const [selectKey, setSelectKey] = useState(0);

        // Update local state when partialInput or remaining changes
        useEffect(() => {
            setLocalPortfolioId(partialInput.portfolioId || '');
            setLocalQty(partialInput.qty || remaining);
            setLocalAmount(stripNoise((partialInput.qty || remaining) * grossPerUnit));
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [partialInput, remaining, grossPerUnit]);

        // Amount (incl. fees) input → units.
        const applyAmount = (amt: number) => {
            setLocalAmount(amt);
            if (!grossPerUnit) return;
            const q = stripNoise(amt / grossPerUnit);
            // Snap to the exact remaining only when the typed amount is within one
            // display-rounding cent of the remaining's value, so the last allocation
            // closes the trade without ever silently moving more than ~0.01 currency.
            setLocalQty(Math.abs(q - remaining) * grossPerUnit < 0.01 ? remaining : q);
        };

        // A cleared input has no way back to the remaining qty otherwise — restore on blur.
        const restoreRemainingOnBlur = () => {
            if (!localQty || localQty <= 0) {
                setLocalQty(remaining);
                setLocalAmount(stripNoise(remaining * grossPerUnit));
            }
        };

        // State to track if Select is open
        const [isSelectOpen, setIsSelectOpen] = useState(false);

        // Create a memoized filtered list of options when the select is opened
        const currentOptions = useMemo(() => {
            if (!isSelectOpen) return []; // Return empty array when not opened
            return portfolioOptions; // Return full list when opened
        }, [portfolioOptions, isSelectOpen]);

        // Custom render function for portfolio options with color indicators and grid layout
        const renderPortfolioOption = useCallback(({ option, checked }: { option: { value: string; label: string }, checked?: boolean }) => {
            const portfolioId = option.value;
            const symbolID = trade.firebaseKey ?? String(trade.symbolCode);
            const selectedQty = localQty > 0 ? localQty : remaining;
            const validation = isAllocationValid(portfolioId, symbolID, trade.side, selectedQty, trade.price, trade.genevaExchange ?? trade.exchange, trade.currency, runningBalances);
            const warnings = portfolioWarnings[portfolioId] || [];
            // Use the snapshot balance for the portfolio
            const currentBalance = runningBalances[portfolioId] || { portfolioId, cash: 0, securities: {} };

            return (
                <PortfolioOption
                    option={option}
                    checked={checked}
                    currentBalance={currentBalance}
                    symbolID={symbolID}
                    symbol={trade.symbol}
                    side={trade.side}
                    price={trade.price}
                    qty={selectedQty}
                    validation={validation}
                    warnings={warnings}
                    currency={trade.currency || 'KWD'}
                />
            );
        }, [trade.firebaseKey, trade.side, trade.price, localQty, remaining, isAllocationValid, portfolioWarnings, runningBalances]);

        // Custom filter function to sort options by availability
        const portfolioOptionsFilter = useCallback((input: { options: any[], search: string }) => {
            // First filter by search text - ensure we handle any type of options
            const options = input.options.map(option => {
                // Handle both simple values and objects with value/label
                if (typeof option === 'string') {
                    return { value: option, label: option };
                } else if (option.value !== undefined && option.label !== undefined) {
                    return { value: option.value, label: option.label };
                } else if (option.group) {
                    // Skip group items, they'll be filtered out anyway
                    return { value: '', label: '' };
                }
                // Default fallback
                return { value: String(option), label: String(option) };
            });

            // Now filter by search text
            const filtered = options.filter((option) =>
                option.label.toLowerCase().includes(input.search.toLowerCase().trim())
            );

            // Then sort by validation status - only process if the option has a valid value
            return filtered.sort((a, b) => {
                if (!a.value || !b.value) return 0;

                // Get validation status for option A
                const validationA = isAllocationValid(
                    a.value,
                    trade.firebaseKey ?? String(trade.symbolCode),
                    trade.side,
                    localQty > 0 ? localQty : remaining,
                    trade.price,
                    trade.genevaExchange ?? trade.exchange,
                    trade.currency,
                    runningBalances
                );
                const warningsA = portfolioWarnings[a.value] || [];

                // Get validation status for option B
                const validationB = isAllocationValid(
                    b.value,
                    trade.firebaseKey ?? String(trade.symbolCode),
                    trade.side,
                    localQty > 0 ? localQty : remaining,
                    trade.price,
                    trade.genevaExchange ?? trade.exchange,
                    trade.currency,
                    runningBalances
                );
                const warningsB = portfolioWarnings[b.value] || [];

                // Scoring: 2 = valid without warnings, 1 = valid with warnings, 0 = invalid
                const scoreA = validationA.valid ? (warningsA.length > 0 ? 1 : 2) : 0;
                const scoreB = validationB.valid ? (warningsB.length > 0 ? 1 : 2) : 0;

                // Sort by score (descending) and then alphabetically for equal scores
                return scoreB - scoreA || a.label.localeCompare(b.label);
            });
        }, [trade.firebaseKey, trade.side, trade.price, localQty, remaining, isAllocationValid, portfolioWarnings, runningBalances]);

        // Check if the allocation is valid
        const validationResult = useMemo(() => {
            if (!localPortfolioId || localQty <= 0) {
                return { valid: true, message: '' }; // Not ready to validate yet
            }

            // Call the validation function
            return isAllocationValid(
                localPortfolioId,
                trade.firebaseKey ?? String(trade.symbolCode),
                trade.side,
                localQty,
                trade.price,
                trade.genevaExchange ?? trade.exchange,
                trade.currency,
                runningBalances
            );
        }, [localPortfolioId, localQty, trade.firebaseKey, trade.side, trade.price, isAllocationValid, runningBalances]);

        // Handle the assign action with local state values
        const handleLocalAssign = () => {
            // Create a custom trade object with the local input values
            const customTrade = {
                ...trade,
                customInput: { portfolioId: localPortfolioId, qty: localQty }
            };

            // Call the original handleAssign with our custom trade object
            // and a callback to update our local loading state
            handleAssign(customTrade, setIsLoading);
            //Reset the local input after assign
            setLocalPortfolioId('');
            // Reset key to reload select 
            setSelectKey(prev => prev + 1);
        };

        // Render warning icon if allocation is invalid
        const warningIcon = !validationResult.valid ? (
            <Tooltip label={validationResult.message} position="top" withArrow>
                <ActionIcon color="yellow" size="sm">
                    <IconAlertTriangle size={16} />
                </ActionIcon>
            </Tooltip>
        ) : null;

        // In-flight / cancelled AllFunds rows are visible but not allocatable —
        // show the live status instead of allocation controls.
        if (isGlobal && !allocatable) {
            const dead = ['Cancelled', 'Error'].includes(trade.allfundsStatus ?? '');
            return (
                <Tooltip
                    label={dead ? 'Order cancelled/errored at AllFunds' : 'In progress at AllFunds — allocatable once NAV settles'}
                    position="top"
                >
                    <Badge color={dead ? 'red' : 'yellow'} variant="light">
                        {trade.allfundsStatus || 'In progress'}
                    </Badge>
                </Tooltip>
            );
        }

        const resultJsx = (
            <Group>
                <Select
                    key={selectKey} // Reset select when options change
                    data={currentOptions}
                    placeholder="Portfolio"
                    value={localPortfolioId}
                    onChange={val => setLocalPortfolioId(val || '')}
                    style={{ width: 120 }}
                    size="xs"
                    searchable
                    comboboxProps={{ width: 500, position: 'bottom-start' }}
                    onDropdownOpen={() => setIsSelectOpen(true)}
                    onDropdownClose={() => setIsSelectOpen(true)}  //let's keep it true so we can see what was selected
                    renderOption={renderPortfolioOption}
                    filter={portfolioOptionsFilter}
                />
                {isGlobal && amountMode ? (
                    <NumberInput
                        value={localAmount || ''}
                        onChange={val => applyAmount(Number(val) || 0)}
                        onBlur={restoreRemainingOnBlur}
                        placeholder={feePerUnit ? 'Amount incl. fees' : 'Amount'}
                        min={0}
                        style={{ width: 110 }}
                        allowNegative={false}
                        hideControls
                        size="xs"
                        thousandSeparator=","
                    />
                ) : (
                    <NumberInput
                        value={localQty || ''}
                        onChange={val => setLocalQty(Number(val) || 0)}
                        onBlur={restoreRemainingOnBlur}
                        placeholder="Qty"
                        min={0}
                        max={remaining}
                        style={{ width: 100 }}
                        allowNegative={false}
                        allowDecimal={isFractional}
                        hideControls
                        size="xs"
                        thousandSeparator=","

                    />
                )}
                {isGlobal && (() => {
                    // The customer's charge is qty × grossPerUnit (invested ± pro-rata fee)
                    const customerTotal = stripNoise(localQty * grossPerUnit);
                    const netAmount = stripNoise(localQty * (trade.price || 0));
                    return (
                        <>
                            <Tooltip label={amountMode ? 'Switch to units input' : 'Switch to amount input'} position="top">
                                <ActionIcon size="xs" variant="subtle" onClick={() => setAmountMode(m => !m)}>
                                    <IconArrowsLeftRight size={12} />
                                </ActionIcon>
                            </Tooltip>
                            <Text size="xs" c="dimmed">
                                {amountMode
                                    ? `= ${fmtQty(localQty)} units${feePerUnit ? ` · ${fmtQty(netAmount)} ${trade.currency || ''} net of fees` : ''}`
                                    : `= ${fmtQty(customerTotal)} ${trade.currency || ''}${feePerUnit ? ' incl. fees' : ''}`}
                            </Text>
                        </>
                    );
                })()}
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    {warningIcon}
                    <Tooltip label="Assign to Portfolio" position="top">
                        <ActionIcon
                            size="sm"
                            variant="filled"
                            disabled={!localPortfolioId || localQty <= 0 || localQty > remaining + QTY_EPSILON || isLoading}
                            onClick={handleLocalAssign}
                            loading={isLoading}
                            aria-label="Assign to Portfolio"
                        >
                            <IconCheck size={14} />
                        </ActionIcon>
                    </Tooltip>
                </div>
            </Group>
        );

        return resultJsx;
    }
);

// Define the props for the new component
interface AggregatedActionCellProps {
    row: any; // Use any type for the row to avoid type conflicts
    selectedRows?: MRT_Row<Trade>[]; // this will be used for the bulk actions on the selected rows and it optional and if it exist it will ignore 'row'
    allocations: Record<string, { portfolioId: string; qty: number }[]>;
    partialInput: Record<string, { portfolioId: string; qty: number }>;
    portfolioOptions: { value: string; label: string }[];
    onPortfolioChange: (id: string, v: string) => void;
    handleAssignBulk: (tradeIds: string[], portfolioId: string, onLoadingChange: (isLoading: boolean) => void) => void;
    handleAssignBulkWithQty: (tradeIds: string[], portfolioId: string, qty: number, onLoadingChange: (isLoading: boolean) => void) => void;
    handleDeleteAllAllocations: (tradeIds: string[], onLoadingChange: (isLoading: boolean) => void) => void;
    portfolioWarnings: Record<string, PortfolioWarning[]>;
    isAllocationValid: (portfolioId: string, symbolID: string, side: 'BUY' | 'SELL', quantity: number, price: number, exchange: string, currency: string | undefined, specificBalances?: Record<string, RunningBalance>) => { valid: boolean; message: string };
    runningBalances: Record<string, RunningBalance>;
}

// Add a reusable component to render portfolio options with details and status
interface PortfolioOptionProps {
    option: { value: string; label: string };
    checked?: boolean;
    currentBalance?: RunningBalance;
    symbolID: string;
    symbol: string;
    side: 'BUY' | 'SELL';
    price: number;
    qty: number;
    validation: { valid: boolean; message: string };
    currency: string; // Currency code (KWD, AED, USD, ...) used to look up cash
    warnings?: PortfolioWarning[];
}

const PortfolioOption: React.FC<PortfolioOptionProps> = React.memo(({
    option,
    checked,
    currentBalance,
    symbolID,
    symbol,
    side,
    price,
    qty,
    validation,
    currency,
    warnings = []
}) => {
    const portfolioId = option.value;
    const labelParts = option.label.split(' - ');
    const portfolioCode = labelParts[0] || portfolioId;
    const portfolioName = labelParts.length > 1 ? labelParts.slice(1).join(' - ') : 'Unknown Name';

    let cashText = '-';
    let securityQtyText = '-';
    let cashTooltip = 'Cash data unavailable';
    let securityTooltip = `${symbol} data unavailable`;

    if (currentBalance) {
        const currencyKey = resolveCurrency(currency);
        const cash = currentBalance.cashMap ? currentBalance.cashMap[currencyKey] || 0 : 0;
        cashText = `${cash.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })} ${currencyKey}`;
        cashTooltip = `Cash: ${cash.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currencyKey}`;

        const security = currentBalance.securities?.[symbolID];
        const quantity = security?.quantity || 0;
        securityQtyText = `${quantity.toLocaleString()}`;
        securityTooltip = `${symbol} Qty: ${securityQtyText}`;
    }

    let textColor = 'green.6';
    let statusIcon = null;
    let tooltipContent = validation.valid ? (warnings.length > 0 ? `This portfolio has allocation issues in the trade sequence` : '') : validation.message;

    if (!validation.valid) {
        textColor = 'red.6';
        statusIcon = <IconAlertTriangle size={14} color="red" />;
    } else if (warnings.length > 0) {
        textColor = 'orange.6';
        statusIcon = <IconAlertTriangle size={14} color="orange" />;
    }

    const colStyle = { borderRight: '1px solid var(--mantine-color-gray-3)', paddingRight: 'var(--mantine-spacing-xs)' };
    const lastColStyle = { paddingLeft: 'var(--mantine-spacing-xs)' };

    // Highlight styles based on trade side
    const cashHighlight = side === 'BUY' ? {
        backgroundColor: 'var(--mantine-color-yellow-0)',
        fontWeight: 1000,
        // borderRadius: 'var(--mantine-radius-sm)',
        // padding: '2px 4px',
    } : {};

    const securityHighlight = side === 'SELL' ? {
        backgroundColor: 'var(--mantine-color-yellow-0)',
        fontWeight: 1000,
        // borderRadius: 'var(--mantine-radius-sm)',
        // padding: '2px 4px',
    } : {};

    const mainContent = (
        <Grid gap="xs" align="center" style={{ flex: 1, color: textColor }}>
            <Grid.Col span={3} style={colStyle}>
                <Text size="xs" fw={checked ? 600 : 400} style={{ whiteSpace: 'nowrap' }}>{portfolioCode}</Text>
            </Grid.Col>
            <Grid.Col span={4} style={colStyle}>
                <Text size="xs" truncate="end" fw={checked ? 600 : 400}>{portfolioName}</Text>
            </Grid.Col>
            <Grid.Col span={3} style={colStyle}>
                <Tooltip label={cashTooltip} position="top">
                    <Text size="xs" ta="right" fw={checked ? 600 : 400} style={cashHighlight}>{cashText}</Text>
                </Tooltip>
            </Grid.Col>
            <Grid.Col span={2} style={lastColStyle}>
                <Tooltip label={securityTooltip} position="top">
                    <Text size="xs" ta="right" fw={checked ? 600 : 400} style={securityHighlight}>{securityQtyText}</Text>
                </Tooltip>
            </Grid.Col>
        </Grid>
    );

    return (
        <Group wrap="nowrap" gap="xs" w="100%">
            {statusIcon ? (
                <Tooltip label={tooltipContent} position="top-start" multiline maw={420}>
                    {statusIcon}
                </Tooltip>
            ) : (
                <Box w={14} />
            )}
            {mainContent}
            {checked && <IconCheck style={{ marginInlineStart: 'auto' }} color="blue" size={16} />}
        </Group>
    );
});

// New component for aggregated actions with on-demand loading of options
const AggregatedActionCell = React.memo(
    ({
        row,
        selectedRows,
        allocations,
        partialInput,
        portfolioOptions,
        onPortfolioChange,
        handleAssignBulk,
        handleAssignBulkWithQty,
        handleDeleteAllAllocations,
        portfolioWarnings,
        isAllocationValid,
        runningBalances,
    }: AggregatedActionCellProps) => {
        // Calculate leafRows first using useMemo
        const leafRows = useMemo(() => {
            try {
                // if selectedRows is provided, use it; otherwise, get leaf rows from the row
                if (selectedRows && selectedRows.length > 0) {
                    return selectedRows;
                }
                return row.getLeafRows ? row.getLeafRows() : [];
            } catch (error) {
                console.error('Error getting leaf rows:', error);
                return [];
            }
        }, [row, selectedRows]);

        // Local loading states
        const [bulkAssignLoading, setBulkAssignLoading] = useState(false);
        const [deleteAllocLoading, setDeleteAllocLoading] = useState(false);

        // Use local state instead of parent state
        const [localPortfolioId, setLocalPortfolioId] = useState('');
        const [localQty, setLocalQty] = useState(() => 0);
        // GLOBAL_DPM rows default to amount-first input (units = amount ÷ NAV)
        const [amountMode, setAmountMode] = useState<boolean | null>(null);
        const [localAmount, setLocalAmount] = useState(0);
        // Fractional units are opt-in per exchange via config; everything else is integer-only.
        const configContext = useContext(ConfigContext);
        // Key to reset Select when options change
        const [selectKey, setSelectKey] = useState(0);

        // State to track if Select is open
        const [isSelectOpen, setIsSelectOpen] = useState(false);

        //variable to check if all leaf rows have the same symbol, side, and price in order to show the quantity input
        const isDivisible = leafRows.every(
            (row: MRT_Row<Trade>) =>
                row.original?.symbol === leafRows[0].original?.symbol &&
                row.original?.side === leafRows[0].original?.side &&
                row.original?.price === leafRows[0].original?.price
        );

        // Create a memoized filtered list of options when the select is opened
        const currentOptions = useMemo(() => {
            if (!isSelectOpen) return []; // Return empty array when not opened
            return portfolioOptions; // Return full list when opened
        }, [portfolioOptions, isSelectOpen]);

        // Memoize the total remaining calculation to avoid recalculating on every render
        const totalRemaining = useMemo(() => {
            const remaining = stripNoise(leafRows.reduce((sum: number, row: MRT_Row<Trade>) => {
                const trade = row.original;
                if (!trade || !row.original?.id || row.original.id.toString() !== row.id) return sum;
                if (!isAllfundsTradeAllocatable(trade)) return sum; // in-flight AllFunds rows aren't allocatable
                const assigned = (allocations[trade.id] || []).reduce((s: number, a) => s + a.qty, 0);
                return sum + (trade.filledQty - assigned);
            }, 0));
            setLocalQty(remaining); // Update local state with the total remaining quantity
            return remaining;
        }, [leafRows, allocations]) as number;

        // Memoize the allocation check to avoid recalculating on every render
        const hasAllocations = useMemo(() => {
            return leafRows.some((row: MRT_Row<Trade>) => {
                return row.original && allocations[row.original.id]?.length > 0;
            });
        }, [leafRows, allocations]);

        // Get the first trade for validation purposes
        const firstTrade = useMemo(() => {
            return leafRows.length > 0 ? leafRows[0].original : null;
        }, [leafRows]);

        // Custom filter for portfolio options in aggregated view
        const aggregatedPortfolioFilter = useCallback((input: { options: any[], search: string }) => {
            //check if divisible in order to show the correct quantity
            const qty = isDivisible ? localQty || totalRemaining : totalRemaining;
            // Handle different option formats
            const options = input.options.map(option => {
                if (typeof option === 'string') {
                    return { value: option, label: option };
                } else if (option.value !== undefined && option.label !== undefined) {
                    return { value: option.value, label: option.label };
                } else if (option.group) {
                    return { value: '', label: '' };
                }
                return { value: String(option), label: String(option) };
            });

            // Filter by search
            const filtered = options.filter((option) =>
                option.label.toLowerCase().includes(input.search.toLowerCase().trim())
            );

            // If no first trade, just return alpha-sorted
            if (!firstTrade) {
                return filtered.sort((a, b) => a.label.localeCompare(b.label));
            }

            // Sort by validation status (valid without warnings > valid with warnings > invalid), then alphabetically
            return filtered.sort((a, b) => {
                if (!a.value || !b.value) return 0;
                const symbolID = firstTrade.firebaseKey ?? String(firstTrade.symbolCode);
                // Compute validation and warnings for A
                const validationA = isAllocationValid(
                    a.value,
                    symbolID,
                    firstTrade.side,
                    qty,
                    firstTrade.price,
                    firstTrade.genevaExchange ?? firstTrade.exchange,
                    firstTrade.currency,
                    runningBalances
                );
                const scoreA = validationA.valid ? (portfolioWarnings[a.value]?.length > 0 ? 1 : 2) : 0;
                // Compute validation and warnings for B
                const validationB = isAllocationValid(
                    b.value,
                    symbolID,
                    firstTrade.side,
                    qty,
                    firstTrade.price,
                    firstTrade.genevaExchange ?? firstTrade.exchange,
                    firstTrade.currency,
                    runningBalances
                );
                const scoreB = validationB.valid ? (portfolioWarnings[b.value]?.length > 0 ? 1 : 2) : 0;
                // Sort by score descending
                if (scoreB !== scoreA) {
                    return scoreB - scoreA;
                }
                // Alphabetical fallback
                return a.label.localeCompare(b.label);
            });
        }, [firstTrade, totalRemaining, isAllocationValid, portfolioWarnings, runningBalances, localQty, isDivisible]);

        // Use reusable PortfolioOption for aggregated options
        const renderAggregatedPortfolioOption = useCallback(({ option, checked }: { option: { value: string; label: string }, checked?: boolean }) => {
            //check if divisible in order to show the correct quantity
            const qty = isDivisible ? localQty || totalRemaining : totalRemaining;

            const portfolioId = option.value;
            const symbolID = firstTrade ? firstTrade.firebaseKey ?? String(firstTrade.symbolCode) : '';
            const validation = firstTrade
                ? isAllocationValid(portfolioId, symbolID, firstTrade.side, qty, firstTrade.price, firstTrade.genevaExchange ?? firstTrade.exchange, firstTrade.currency, runningBalances)
                : { valid: true, message: '' };
            const warnings = portfolioWarnings[portfolioId] || [];
            // Use the snapshot balance for the portfolio 
            const currentBalance = runningBalances[portfolioId] || { portfolioId, cash: 0, securities: {} };

            return (
                <PortfolioOption
                    option={option}
                    checked={checked}
                    currentBalance={currentBalance}
                    symbolID={symbolID}
                    symbol={firstTrade?.symbol || symbolID}
                    side={firstTrade?.side}
                    price={firstTrade?.price}
                    qty={qty}
                    validation={validation}
                    currency={firstTrade.currency || 'KWD'}
                    warnings={warnings}
                />
            );
        }, [firstTrade, totalRemaining, isAllocationValid, portfolioWarnings, runningBalances, localQty, isDivisible]);

        // Extract trade IDs from leaf rows and ignore locked, error, or in-flight AllFunds trades
        const tradeIds = leafRows
            .filter((row: MRT_Row<Trade>) => row.original?.id && row.original.id.toString() === row.id && isAllfundsTradeAllocatable(row.original))
            .map((row: MRT_Row<Trade>) => row.original?.id).filter(Boolean);

        const isGlobalGroup = leafRows.length > 0 && leafRows.every(
            (r: MRT_Row<Trade>) => !r.original || r.original.accountId === 'GLOBAL_DPM'
        );
        const isFractional = (configContext.tradesMap?.fractionalExchanges ?? []).includes(firstTrade?.exchange ?? '');
        const effectiveAmountMode = amountMode ?? isGlobalGroup;
        const groupPrice = firstTrade?.price || 0;
        // A cleared input has no way back to the remaining qty otherwise — restore on blur.
        const restoreGroupRemainingOnBlur = () => {
            if (!localQty || localQty <= 0) {
                setLocalQty(totalRemaining);
                setLocalAmount(stripNoise(totalRemaining * groupPrice));
            }
        };
        const applyGroupAmount = (amt: number) => {
            setLocalAmount(amt);
            if (!groupPrice) return;
            const q = stripNoise(amt / groupPrice);
            // Snap to the exact remaining only when the typed amount is within one
            // display-rounding cent of the remaining's value (never silently moves
            // more than ~0.01 currency).
            setLocalQty(Math.abs(q - totalRemaining) * groupPrice < 0.01 ? totalRemaining : q);
        };



        if (tradeIds.length === 0) {
            return <Text c="dimmed">No trades in this group</Text>;
        }


        // Local handler for bulk assign that uses the local portfolioId
        const handleLocalBulkAssign = () => {
            // Call the parent handler with the trade IDs, local portfolio ID, and loading callback
            handleAssignBulk(tradeIds, localPortfolioId, setBulkAssignLoading);

            // Reset the local input after assign
            setLocalPortfolioId('');
            // Reset key to reload select 
            setSelectKey(prev => prev + 1);
        };


        // Local handler for bulk assign that uses the local portfolioId
        const handleLocalBulkQtyAssign = () => {
            // Call the parent handler with the trade IDs, local portfolio ID, and loading callback
            handleAssignBulkWithQty(tradeIds, localPortfolioId, localQty, setBulkAssignLoading);

            // Reset the local input after assign
            setLocalPortfolioId('');
            // Reset key to reload select 
            setSelectKey(prev => prev + 1);
        };

        // Local handler for delete all allocations
        const handleLocalDeleteAll = () => {
            handleDeleteAllAllocations(tradeIds, setDeleteAllocLoading);
        };


        if (isDivisible) {
            return (
                <Group>
                    <Select
                        key={selectKey} // Reset select when options change
                        data={currentOptions}
                        placeholder='Portfolio'
                        value={localPortfolioId}
                        onChange={val => setLocalPortfolioId(val || '')}
                        style={{ width: 120 }}
                        comboboxProps={{ width: 500, position: 'bottom-start' }}

                        size="xs"
                        searchable
                        limit={10}
                        onDropdownOpen={() => setIsSelectOpen(true)}
                        onDropdownClose={() => setIsSelectOpen(true)} //let's keep it true so we can see what was selected
                        renderOption={renderAggregatedPortfolioOption}
                        filter={aggregatedPortfolioFilter}
                    />
                    {isGlobalGroup && effectiveAmountMode ? (
                        <NumberInput
                            value={localAmount || ''}
                            onChange={val => applyGroupAmount(Number(val) || 0)}
                            onBlur={restoreGroupRemainingOnBlur}
                            placeholder="Amount"
                            min={0}
                            style={{ width: 100 }}
                            allowNegative={false}
                            hideControls
                            size="xs"
                            thousandSeparator=","
                            disabled={totalRemaining <= 0 || bulkAssignLoading}
                        />
                    ) : (
                        <NumberInput
                            value={localQty || ''}
                            onChange={val => setLocalQty(Number(val) || 0)}
                            onBlur={restoreGroupRemainingOnBlur}
                            placeholder="Qty"
                            min={0}
                            max={totalRemaining}
                            style={{ width: 100 }}
                            allowNegative={false}
                            allowDecimal={isFractional}
                            hideControls
                            size="xs"
                            thousandSeparator=","
                            disabled={totalRemaining <= 0 || bulkAssignLoading}

                        />
                    )}
                    {isGlobalGroup && (
                        <>
                            <Tooltip label={effectiveAmountMode ? 'Switch to units input' : 'Switch to amount input'} position="top">
                                <ActionIcon size="xs" variant="subtle" onClick={() => setAmountMode(!effectiveAmountMode)}>
                                    <IconArrowsLeftRight size={12} />
                                </ActionIcon>
                            </Tooltip>
                            <Text size="xs" c="dimmed">
                                {effectiveAmountMode
                                    ? `= ${fmtQty(localQty)} units`
                                    : `= ${fmtQty(stripNoise(localQty * groupPrice))} ${firstTrade?.currency || ''}`}
                            </Text>
                        </>
                    )}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        {/* {warningIcon} */}
                        <Tooltip label="Assign to Portfolio" position="top">
                            <ActionIcon
                                size="sm"
                                variant="filled"
                                disabled={!localPortfolioId || localQty <= 0 || localQty > totalRemaining + QTY_EPSILON || bulkAssignLoading}
                                onClick={handleLocalBulkQtyAssign}
                                loading={bulkAssignLoading}
                                aria-label="Assign to Portfolio"
                            >
                                <IconCheck size={14} />
                            </ActionIcon>
                        </Tooltip>
                    </div>
                    <Tooltip label="Delete All Allocations" position="top">
                        <ActionIcon
                            size="sm"
                            variant="filled"
                            color="red"
                            disabled={!hasAllocations || deleteAllocLoading}
                            onClick={handleLocalDeleteAll}
                            loading={deleteAllocLoading}
                            aria-label="Delete All Allocations"
                        >
                            <IconTrash size={14} />
                        </ActionIcon>
                    </Tooltip>
                </Group>
            );
        }



        return (
            <Group>
                <Select
                    key={selectKey} // Reset select when options change
                    data={currentOptions}
                    placeholder='Portfolio'
                    value={localPortfolioId}
                    onChange={val => setLocalPortfolioId(val || '')}
                    style={{ width: 120 }}
                    comboboxProps={{ width: 500, position: 'bottom-start' }}

                    size="xs"
                    searchable
                    limit={10}
                    onDropdownOpen={() => setIsSelectOpen(true)}
                    onDropdownClose={() => setIsSelectOpen(true)} //let's keep it true so we can see what was selected
                    renderOption={renderAggregatedPortfolioOption}
                    filter={aggregatedPortfolioFilter}
                />
                <Tooltip label="Assign All to Portfolio" position="top">
                    <ActionIcon
                        size="sm"
                        variant="filled"
                        color="indigo"
                        disabled={!localPortfolioId || totalRemaining <= 0 || bulkAssignLoading}
                        onClick={handleLocalBulkAssign}
                        loading={bulkAssignLoading}
                        aria-label="Assign All to Portfolio"
                    >
                        <IconPlus size={14} />
                    </ActionIcon>
                </Tooltip>
                <Tooltip label="Delete All Allocations" position="top">
                    <ActionIcon
                        size="sm"
                        variant="filled"
                        color="red"
                        disabled={!hasAllocations || deleteAllocLoading}
                        onClick={handleLocalDeleteAll}
                        loading={deleteAllocLoading}
                        aria-label="Delete All Allocations"
                    >
                        <IconTrash size={14} />
                    </ActionIcon>
                </Tooltip>
            </Group>
        );
    }
);

const getRemainingQty = ((trade: Trade, allocation: { portfolioId: string; qty: number }[]) => {
    const assigned = allocation.reduce((sum, a) => sum + a.qty, 0);
    const remaining = stripNoise(trade.filledQty - assigned);
    // snap float residue to an exact zero so fully-allocated fractional trades close cleanly
    return Math.abs(remaining) < QTY_EPSILON ? 0 : remaining;
});

// Component for the removal button with its own loading state
const RemoveAllocButton = React.memo(({ trade, portfolioId, onRemove }: {
    trade: Trade;
    portfolioId: string;
    onRemove: (trade: Trade, portfolioId: string, onLoadingChange: (isLoading: boolean) => void) => void;
}) => {
    const [isLoading, setIsLoading] = useState(false);

    const handleClick = () => {
        onRemove(trade, portfolioId, setIsLoading);
    };

    return (
        <ActionIcon
            size="xs"
            onClick={handleClick}
            loading={isLoading}
            c={'white'}
            variant='transparent'
        >
            <IconX size={12} />
        </ActionIcon>
    );
});



interface TradesTableProps {
    preSelectedDate?: Date;
    readonly?: boolean;
    showAllAccountsTrades?: boolean; // If true, show trades from all accounts
    expanded?: boolean;
    preSelectedAccountId?: string;
    onWarningsChange?: (warnings: Record<string, PortfolioWarning[]>) => void;
}

export const TradesTable: React.FC<TradesTableProps> = ({
    preSelectedDate,
    readonly = false,
    showAllAccountsTrades = false,
    expanded = true,
    preSelectedAccountId,
    onWarningsChange,
}) => {
    const db = useFirestore();
    const userContext = useContext(MyUserDataContext);
    const configContext = useContext(ConfigContext);
    let userClaims = userContext.firebaseTokenResult?.claims.customClaims as CustomClaims || {};
    const allocationsRef = useRef<Record<string, { portfolioId: string; qty: number }[]>>({});


    const [availableDates, setAvailableDates] = useState<Date[]>([]);
    const [tradeDateDocs, setTradeDateDocs] = useState<Record<string, TradeDate>>({});
    const [date, setDate] = useState<Date | null>(null);
    const [trades, setTrades] = useState<Trade[]>([]);
    const [symbolMap, setSymbolMap] = useState<Record<string, SecurityInfo>>({});
    const [allocations, setAllocations] = useState<Record<string, { portfolioId: string; qty: number }[]>>({});
    const [partialInput, setPartialInput] = useState<Record<string, { portfolioId: string; qty: number }>>({});
    const [loading, setLoading] = useState<string | null>(null);
    const [portfolioLoading, setPortfolioLoading] = useState<string | null>(null);
    const [runningBalances, setRunningBalances] = useState<Record<string, RunningBalance>>({});
    // Per-trade running balance snapshots
    const [runningSnapshots, setRunningSnapshots] = useState<Record<string, Record<string, RunningBalance>>>({});
    const [portfolioWarnings, setPortfolioWarnings] = useState<Record<string, PortfolioWarning[]>>({});
    const [filterTicker, setFilterTicker] = useState<string | null>(null);
    const [filterDealer, setFilterDealer] = useState('');
    const [tradesAcordion, setTradesAcordion] = useState<string | null>(expanded ? 'trades' : null);
    const [excelAllocationLoading, setExcelAllocationLoading] = useState(false);
    const [excelAllocationModalOpened, setExcelAllocationModalOpened] = useState(false);
    const [globalDpmUploadOpened, setGlobalDpmUploadOpened] = useState(false);
    const [globalDpmUploadsListOpened, setGlobalDpmUploadsListOpened] = useState(false);
    const [tableToShow, setTableToShow] = useState<'normalTrades' | 'manualTrades'>('normalTrades');

    // 1. Create a ref to hold the latest portfolioWarnings
    const portfolioWarningsRef = useRef(portfolioWarnings);

    // 2. Keep the ref in sync with the state whenever it changes
    useEffect(() => {
        portfolioWarningsRef.current = portfolioWarnings;
    }, [portfolioWarnings]);

    const [filterSide, setFilterSide] = useState<'BUY' | 'SELL' | ''>(() => {
        // Read initial side filter from localStorage or default to ''
        try {
            const savedSide = localStorage.getItem('tradesTableSideFilter');
            // Ensure the saved value is one of the allowed types
            if (savedSide === 'BUY' || savedSide === 'SELL') {
                return savedSide;
            }
            return ''; // Default to empty string if not valid
        } catch (e) {
            console.error("Error reading side filter from localStorage", e);
            return '';
        }
    });
    const [grouping, setGrouping] = useState<MRT_GroupingState>(() => {
        // Read initial grouping from localStorage or default
        try {
            const savedGrouping = localStorage.getItem('tradesTableGrouping');
            return savedGrouping ? JSON.parse(savedGrouping) : ['orderId'];
        } catch (error) {
            console.error("Error reading grouping state from localStorage", error);
            return ['orderId']; // Default grouping
        }
    });
    const [sorting, setSorting] = useState(() => {
        // Read initial sorting from localStorage or default
        try {
            const savedSorting = localStorage.getItem('tradesTableSorting');
            return savedSorting ? JSON.parse(savedSorting) : [];
        } catch (error) {
            console.error("Error reading sorting state from localStorage", error);
            return [];
        }
    });
    const [columnFilters, setColumnFilters] = useState(() => {
        // Read initial column filters from localStorage or default
        try {
            const savedFilters = localStorage.getItem('tradesTableFilters');
            return savedFilters && !readonly ? JSON.parse(savedFilters) : [];
        } catch (error) {
            console.error("Error reading filters state from localStorage", error);
            return [];
        }
    });
    const [columnSizing, setColumnSizing] = useState(() => {
        // Read initial column sizing from localStorage or default
        try {
            const savedSizing = localStorage.getItem('tradesTableColumnSizing');
            return savedSizing ? JSON.parse(savedSizing) : {};
        } catch (error) {
            console.error("Error reading column sizing state from localStorage", error);
            return {};
        }
    });
    const [columnVisibility, setColumnVisibility] = useState(() => {
        // Read initial column visibility from localStorage or default.
        // Hidden-by-default columns are merged UNDER the saved state so a user who
        // never touched them gets the default, while an explicit show/hide sticks.
        const hiddenByDefault = { accountId: false, netSettle: false };
        try {
            const savedVisibility = localStorage.getItem('tradesTableColumnVisibility');
            return savedVisibility ? { ...hiddenByDefault, ...JSON.parse(savedVisibility) } : hiddenByDefault;
        } catch (error) {
            console.error("Error reading column visibility state from localStorage", error);
            return hiddenByDefault;
        }
    });
    const [allPortfolios, setAllPortfolios] = useState<Portfolio[]>([]);
    const [addModalOpened, setAddModalOpened] = useState(false);

    const [combinedPortfolioGroup, setCombinedPortfolioGroup] = useState<CombinedPortfolioGroup[]>([]);
    const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({});
    const [allAssetManagers, setAllAssetManagers] = useState<{
        value: string;
        label: string;
    }[]>([]);
    const [selectedAssetManager, setSelectedAssetManager] = useState<string>('');

    // Create a memoized map of portfolio IDs to portfolio codes for efficient lookups
    const portfolioCodeMap = useMemo(() => {
        const map: Record<string, Portfolio> = {};
        allPortfolios.forEach(p => {
            if (p.id && p.code) {
                // console.warn("portfolioCodeMap", p.id, p.code);
                map[p.id] = p;
            }
        });
        return map;
    }, [allPortfolios]);

    const filteredPortfolios = useMemo(() => {
        return allPortfolios.filter(p => matchesAssetManagerFilter(p, selectedAssetManager));
    }, [allPortfolios, selectedAssetManager]);

    // O(1) lookup for the date picker's excludeDate — it runs per calendar cell per render
    const availableDateStrings = useMemo(
        () => new Set(availableDates.map(d => d.toDateString())),
        [availableDates]
    );

    const selectedTrades = useMemo(() => {
        return trades.filter(t => rowSelection[t.id]);
    }, [trades, rowSelection]);

    // assuming this lives inside your component:
    const uniqueWarnings = useMemo(() => {
        const seen = new Set<string>();
        const result: Array<{
            accountId: string;
            portfolioId: string;
            message: string;
            timestamp: number;
            side: 'BUY' | 'SELL';
            symbolID: string;
        }> = [];

        Object.entries(portfolioWarnings).forEach(
            ([portfolioId, warnings]) => {
                warnings.forEach(w => {
                    // build a key that captures the fields you care about
                    const key = `${portfolioId}|${w.message}`;
                    if (!seen.has(key)) {
                        seen.add(key);
                        result.push({ portfolioId, ...w });
                    }
                });
            }
        );

        const map = new Map<string, Set<string>>();
        result.forEach(({ portfolioId, message }) => {
            if (!map.has(portfolioId)) {
                map.set(portfolioId, new Set());
            }
            map.get(portfolioId)!.add(message);
        });
        const warnings = Array.from(map.entries()).map(([portfolioId, msgs]) => ({
            portfolioId,
            messages: Array.from(msgs),
        }));
        return warnings;

        // return groups;
    }, [portfolioWarnings]);

    // Notify the parent (e.g. settings download page) whenever warnings change.
    // Done in an effect, not inside the memo above: setState during render gets
    // dropped/warned by React and the settings page would miss warnings.
    useEffect(() => {
        if (onWarningsChange) {
            onWarningsChange(portfolioWarnings);
        }
    }, [portfolioWarnings, onWarningsChange]);

    // Update the ref whenever allocations change
    useEffect(() => {
        allocationsRef.current = allocations;
    }, [allocations]);

    // Fetch all securities once
    useEffect(() => {
        if (!date) return;

        const dateID = format(date, 'dd-MM-yyyy');
        const secStart = perf.start('Fetching all securities');
        let unsubscribe: (() => void) | null = null;

        // Check if date-specific securities exist
        getDocs(query(collection(db, 'trade_dates', dateID, 'securities'), where('_INVESTMENTTYPE_CODE_', '==', 'CS'), limit(1)))
            .then(snapshot => {
                const useMainCollection = snapshot.empty;
                const targetCollection = useMainCollection
                    ? collection(db, 'securities')
                    : collection(db, 'trade_dates', dateID, 'securities');

                console.log(`Using ${useMainCollection ? 'main' : 'date-specific'} securities collection`);

                // Set up real-time listener on chosen collection
                unsubscribe = onSnapshot(
                    query(targetCollection, where('_INVESTMENTTYPE_CODE_', '==', 'CS')),
                    (snap) => {
                        // Skip processing if data is from cache
                        if (snap.metadata.fromCache) {
                            console.log('Ignoring cached data, waiting for server response');
                            return;
                        }

                        const map: Record<string, SecurityInfo> = {};
                        snap.forEach(doc => {
                            const data = doc.data();
                            map[doc.id] = { ticker: data.TICKER, name: data._ISSUER_NAMESORT_ };
                        });

                        setSymbolMap(map);
                        perf.log('Loaded securities count', snap.size);
                        secStart();
                    },
                    (error) => {
                        console.error('Error in securities snapshot:', error);
                    }
                );
            })
            .catch(error => {
                console.error('Error checking securities:', error);
            });

        // Cleanup function
        return () => {
            if (unsubscribe) {
                unsubscribe();
            }
        };
    }, [date]);

    // Fetch dates once
    useEffect(() => {
        const endFetch = perf.start('Fetching trade_dates');
        setLoading("Getting trade dates");
        getDocs(collection(db, 'trade_dates')).then(snap => {
            // Skip processing if data is from cache
            if (snap.metadata.fromCache) {
                console.log('Ignoring cached data, waiting for server response');
                return;
            }
            const dates = snap.docs.map(d => parse(d.id, 'dd-MM-yyyy', new Date()));
            dates.sort((a, b) => +b - +a);
            setAvailableDates(dates);
            setTradeDateDocs(Object.fromEntries(snap.docs.map(d => [d.id, d.data() as TradeDate])));
            if (dates.length) {
                setDate(preSelectedDate || dates[0]);
            }
            setLoading(null);
            perf.log('Loaded dates count', dates.length);
            endFetch();
        });
    }, []);

    // Update date when preSelectedDate changes
    useEffect(() => {
        if (preSelectedDate && availableDates.length > 0) {
            // Check if the preSelectedDate is available
            const isDateAvailable = availableDates.some(d => d.toDateString() === preSelectedDate.toDateString());
            if (isDateAvailable) {
                setDate(preSelectedDate);
            }
        }
    }, [preSelectedDate, availableDates]);

    // Fetch Portfolios once
    useEffect(() => {
        if (!date) return;

        const portfolioTypeCodes = getAllowedPortfolioTypeCodes(userContext, configContext);

        const dateID = format(date, 'dd-MM-yyyy');
        const endFetch = perf.start('Fetching portfolios');
        let unsubscribe: (() => void) | null = null;

        // Check if date-specific portfolios exist
        getDocs(query(collection(db, 'trade_dates', dateID, 'portfolios'), limit(1)))
            .then(snapshot => {
                const useMainCollection = snapshot.empty;
                const baseCollection = useMainCollection
                    ? collection(db, 'portfolios')
                    : collection(db, 'trade_dates', dateID, 'portfolios');

                console.log(`Using ${useMainCollection ? 'main' : 'date-specific'} portfolios collection`);

                if (portfolioTypeCodes.length === 0 && !userClaims.admin && !userClaims.oper) {
                    setAllPortfolios([]);
                    setAllAssetManagers([{ value: '', label: 'All Portfolios' }]);
                    return;
                }

                // No server-side portfolioTypeCode clause: the effective type is
                // portfolioContract ?? portfolioTypeCode, which Firestore can't express —
                // filtered client-side below. The trades query's accountId gate remains
                // the security boundary (admin/oper already read portfolios unfiltered).
                const q = query(
                    baseCollection,
                    where('status', '==', 'Active'),
                );

                setPortfolioLoading("Getting portfolios for " + dateID);

                // Set up real-time listener on chosen collection
                unsubscribe = onSnapshot(
                    q,

                    async (snap) => {
                        // Skip processing if data is from cache
                        setPortfolioLoading(null);

                        if (snap.metadata.fromCache) {
                            console.log('Ignoring cached data, waiting for server response');
                            // return;
                        }

                        const assetsManagers: Set<string> = new Set();
                        // Get portfolios from snapshot
                        const portfoliosPromises = snap.docs.map(async (d) => {
                            const portfolioData = d.data() as Portfolio;
                            if (portfolioData.managerName && portfolioData.managerName !== '') {
                                assetsManagers.add(portfolioData.managerName);
                            }
                            portfolioData.id = d.id;

                            return portfolioData;
                        });

                        // Resolve all promises
                        let portfolios = await Promise.all(portfoliosPromises);
                        const seen = new Set<string>();

                        console.log("Portfolios length before filter ", portfolios.length);
                        portfolios = portfolios.filter(item => {
                            if (seen.has(item.code ?? '')) return false;
                            // Non-admins only see portfolios whose effective type is allowed
                            if (!userClaims.admin && !userClaims.oper &&
                                !portfolioTypeCodes.includes(getEffectivePortfolioType(item) ?? '')) return false;
                            // Filter out portfolios with total capital market value <= MIN_PORTFOLIO_VALUE_KD.
                            // Global DPM portfolios are exempt: fresh ones have wired their cash
                            // out to AllFunds and legitimately sit near zero.
                            if (!item.portfolioContract &&
                                (item.position?.total_capital_market_value ?? 0) <= MIN_PORTFOLIO_VALUE_KD) return false;
                            seen.add(item.code ?? '');
                            return true;
                        });
                        console.log("Portfolios length after filter ", portfolios.length);

                        perf.log("Loaded portfolios count", portfolios.length);

                        setAllPortfolios(portfolios);
                        let assetsManagersArray = Array.from(assetsManagers).map((manager) => (
                            { value: manager, label: `${manager} Portfolios` }
                        ));
                        assetsManagersArray.unshift({
                            value: GLOBAL_DPM_MANAGER_FILTER,
                            label: 'Global DPM Portfolios',
                        });
                        assetsManagersArray.unshift({
                            value: '',
                            label: 'All Portfolios',
                        });
                        setAllAssetManagers(assetsManagersArray);
                        if (!readonly && userContext.user?.email && configContext.tradesMap?.assetsManagers && configContext.tradesMap?.assetsManagers[userContext.user.email] && Array.from(assetsManagers).includes(configContext.tradesMap.assetsManagers[userContext.user?.email])) {
                            setSelectedAssetManager(configContext.tradesMap.assetsManagers[userContext.user.email]);
                        } else {
                            setSelectedAssetManager(assetsManagersArray[0].value);
                        }

                        // Initialize running balances from the portfolios
                        const initialBalances = initializeRunningBalances(portfolios);
                        setRunningBalances(initialBalances)

                        endFetch();
                    },
                    (error) => {
                        console.error('Error in portfolios snapshot:', error);
                        setPortfolioLoading(null);
                    }
                );
            })
            .catch(error => {
                console.error('Error fetching portfolios:', error);
                setPortfolioLoading(null);
            });

        // Clean up subscription on unmount
        return () => {
            if (unsubscribe) {
                unsubscribe();
            }
        };
    }, [date]);

    // Initialize running balances when portfolios are loaded
    useEffect(() => {
        if (allPortfolios.length === 0) return;

        const balances: Record<string, RunningBalance> = {};

        allPortfolios.forEach(portfolio => {
            if (!portfolio.id) return;

            const securities: Record<string, SecurityBalance> = {};
            const cashMap: Record<string, number> = {};

            // Initialize securities from local_position
            portfolio.position?.local_position?.forEach(position => {
                if (position.firebase_key && position.code && position.code !== 'KWD' && !position.cash) {
                    securities[position.firebase_key] = {
                        id: position.firebase_key,
                        quantity: position.quantity || 0,
                        name: position.name || ''
                    };
                } else if (position.code === 'KWD' && position.cash) {
                    const currencyKey = resolveCurrency(position.currency || position.code);
                    cashMap[currencyKey] = (cashMap[currencyKey] || 0) + (position.quantity || 0);
                }
            });

            // Add foreign positions if needed
            portfolio.position?.foreign_position?.forEach(position => {
                if (position.firebase_key && position.code && !position.cash) {
                    securities[position.firebase_key] = {
                        id: position.firebase_key,
                        quantity: position.quantity || 0,
                        name: position.name || ''
                    };
                } else if (position.cash && (position.currency || position.code)) {
                    const currencyKey = resolveCurrency(position.currency || position.code);
                    cashMap[currencyKey] = (cashMap[currencyKey] || 0) + (position.quantity || 0);
                }
            });

            balances[portfolio.id] = {
                portfolioId: portfolio.id,
                cashMap,
                securities
            };
        });

        setRunningBalances(balances);
        console.log('Running balances initialized:', balances);
    }, [allPortfolios]);

    // Fetch trades on date/view change

    // Optimized useEffect for fetching trades
    useEffect(() => {
        if (!date) return;
        const endFetch = perf.start('Fetching trades for date');
        const id = format(date, 'dd-MM-yyyy');

        setLoading("Getting all trades for " + id);

        const tradesRef = collection(db, 'trade_dates', id, 'trades');
        let constraints: (QueryFieldFilterConstraint | QueryOrderByConstraint)[] = [];

        if (!userClaims.admin && !userClaims.oper && !showAllAccountsTrades) {

            if (userContext.availableTradeAccounts && userContext.availableTradeAccounts.length > 0) {
                const availableAccounts = userContext.availableTradeAccounts ?? [];
                constraints.push(where('accountId', 'in', availableAccounts));
            } else {
                console.error('No available accounts for user');
                setLoading(null);
                return;
            }
        }

        const q = query(tradesRef, ...constraints, orderBy('execId'));

        // First load - clear previous state and set loading
        setTrades([]);
        setAllocations({});
        setPartialInput({});
        allocationsRef.current = {};

        // Clear symbol cache on date change to prevent memory growth
        symbolComponentCache = {};
        console.log('Symbol cache cleared due to date change');

        // Keep track of current trades for comparison
        let currentTradesMap = new Map<string, Trade>();
        let isFirstLoad = true;

        const unsubscribe = onSnapshot(q, { includeMetadataChanges: true }, snap => {
            // Skip processing if data is from cache or is a local write
            if (snap.metadata.fromCache || snap.metadata.hasPendingWrites) {
                console.log('Ignoring local write or cached data');
                return;
            }

            perf.log('Trades loaded from Firestore', snap.docs.length);
            const memoizeStart = perf.start('Processing loaded trades');

            // On first load, initialize with all documents
            if (isFirstLoad) {
                isFirstLoad = false;

                // Initial load - set up from all docs
                const allTrades: EmbellishedTrade[] = snap.docs.map(d => {
                    const data = d.data() as Trade;
                    currentTradesMap.set(data.id.toString(), data);

                    const unallocQty = (data.allocations || []).reduce(
                        (sum, allocation) => sum + (typeof allocation.qty === 'number' ? allocation.qty : 0),
                        0
                    );
                    return {
                        ...data,
                        formattedTime: format(data.tradeDate.toDate(), 'HH:mm'),
                        formattedPrice: data.currency ? `${data.price?.toFixed(2)} ${data.currency}` : data.price?.toFixed(2),
                        formattedQty: fmtQty(data.filledQty),
                        formattedUnallocQty: fmtQty(unallocQty)
                    };
                });

                // Set initial trades
                setTrades(allTrades);


                // Initialize allocations and input state
                const allocInit: Record<string, { portfolioId: string; qty: number }[]> = {};
                const partInit: Record<string, { portfolioId: string; qty: number }> = {};

                allTrades.forEach(t => {
                    allocInit[t.id] = t.allocations ?? [];
                    partInit[t.id] = { portfolioId: '', qty: getRemainingQty(t, allocInit[t.id] || []) };
                });

                setAllocations(allocInit);
                setPartialInput(partInit);
            }
            // For subsequent updates, process document changes incrementally
            else if (snap.docChanges().length > 0) {
                let hasSignificantChanges = false;
                const significantChanges: Array<{ type: string; data: Trade }> = [];

                // First pass: check for significant changes
                snap.docChanges().forEach(change => {
                    const newData = change.doc.data() as Trade;
                    const tradeId = newData.id.toString();
                    const oldData = currentTradesMap.get(tradeId);

                    if (change.type === 'added' || change.type === 'removed') {
                        hasSignificantChanges = true;
                        significantChanges.push({ type: change.type, data: newData });

                        if (change.type === 'added') {
                            currentTradesMap.set(tradeId, newData);
                        } else {
                            currentTradesMap.delete(tradeId);
                        }
                    } else if (change.type === 'modified' && oldData) {
                        // Use the current allocations from our ref for comparison
                        const currentAllocations = allocationsRef.current[tradeId];

                        // Check if this is a significant change
                        if (hasSignificantTradeChanges(oldData, newData, currentAllocations)) {
                            hasSignificantChanges = true;
                            significantChanges.push({ type: change.type, data: newData });
                        }
                        // Always update the map to keep it in sync
                        currentTradesMap.set(tradeId, newData);
                    }
                });

                // Only update state if there are significant changes
                if (hasSignificantChanges) {
                    console.log(`Processing ${significantChanges.length} significant changes out of ${snap.docChanges().length} total changes`);

                    // Update trades state preserving references for unchanged items
                    setTrades(prev => {
                        const tradeMap = new Map(prev.map(t => [t.id.toString(), t]));

                        // Process only significant changes
                        significantChanges.forEach(({ type, data }) => {
                            const tradeId = data.id.toString();

                            if (type === 'added' || type === 'modified') {
                                const unallocQty = (data.allocations || []).reduce(
                                    (sum, allocation) => sum + (typeof allocation.qty === 'number' ? allocation.qty : 0),
                                    0
                                );
                                const trade: EmbellishedTrade = {
                                    ...data,
                                    formattedTime: format(data.tradeDate.toDate(), 'HH:mm'),
                                    formattedPrice: data.currency ? `${data.price?.toFixed(2)} ${data.currency}` : data.price?.toFixed(2),
                                    formattedQty: fmtQty(data.filledQty),
                                    formattedUnallocQty: fmtQty(unallocQty)
                                };
                                tradeMap.set(tradeId, trade);
                            } else if (type === 'removed') {
                                tradeMap.delete(tradeId);
                            }
                        });

                        // Convert Map back to Array and maintain sort order
                        const updatedTrades = Array.from(tradeMap.values());
                        updatedTrades.sort((a, b) => a.tradeDate.seconds - b.tradeDate.seconds);
                        return updatedTrades;
                    });

                    // Update allocations state only for significant changes
                    significantChanges.forEach(({ type, data }) => {
                        const tradeId = data.id.toString();

                        if (type === 'added' || type === 'modified') {
                            setAllocations(prev => ({
                                ...prev,
                                [tradeId]: data.allocations ?? []
                            }));

                            if (type === 'added') {
                                setPartialInput(prev => ({
                                    ...prev,
                                    [tradeId]: { portfolioId: '', qty: getRemainingQty(data, data.allocations || []) }
                                }));
                            }
                        } else if (type === 'removed') {
                            setAllocations(prev => {
                                const next = { ...prev };
                                delete next[tradeId];
                                return next;
                            });

                            setPartialInput(prev => {
                                const next = { ...prev };
                                delete next[tradeId];
                                return next;
                            });
                        }
                    });
                } else {
                    console.log('No significant changes detected, skipping state update');
                }
            }

            memoizeStart();
            setLoading(null);
            endFetch();
        }, error => {
            console.error('Error in trades snapshot:', error);
            setLoading(null);
            endFetch();
        });

        // Return cleanup function
        return () => unsubscribe();
    }, [date, preSelectedDate, preSelectedAccountId]);

    // Portfolio balances
    const portfolioBalances = useMemo(
        () => {
            const balStart = perf.start('Computing portfolio balances');
            const result = allPortfolios.map(p => {
                const bal = (p?.position?.cash ?? 0) + trades.reduce((sum, t) => {
                    const sign = t.side === 'BUY' ? -1 : 1;
                    return sum + (allocations[t.id] || []).reduce(
                        (s, a) => a.portfolioId === p.id ? s + sign * getTradeCashAmount(t.currency, t.price, a.qty) : s,
                        0
                    );
                }, 0);
                return { ...p, balance: bal };
            });
            balStart();
            return result;
        },
        [trades, allocations, allPortfolios]
    );



    // Add confirmation modal
    const { confirm, ConfirmationModal } = useConfirmation();

    // Track if we're in an undo/redo operation
    const isUndoRedoOperation = useRef(false);

    const pushActionRef = useRef<null | ((
        action: AllocationAction,
        description: string,
        userId?: string
    ) => void)>(null);
    // Callback to apply actions (both forward and inverse) to Firestore and local state
    // ✅ Centralized applyAllocationAction
    // ✅ applyAllocationAction stays centralized and now uses pushActionRef
    const applyAllocationAction = useCallback(async (action: AllocationAction): Promise<boolean> => {
        if (!date) return false;

        const findTrade = (id: string) => trades?.find(t => t.id === id);
        const isLocked = (id: string) => {
            const t = findTrade(id);
            return !!(t && ((t as any).locked === true || (t as any).state === 'LOCKED'));
        };
        const sumQty = (arr: Array<{ qty: number }>) => arr.reduce((s, a) => s + (a?.qty ?? 0), 0);

        const affectedTradeIds: string[] = (() => {
            const fromAllocations =
                action.type === 'BULK_ADD' || action.type === 'BULK_REMOVE'
                    ? Array.from(new Set(action.allocations.map(a => a.tradeId)))
                    : action.type === 'ADD_ALLOCATION' || action.type === 'REMOVE_ALLOCATION'
                        ? [action.tradeId]
                        : [];

            const fromSelection = Array.isArray(action.selectedTradeIds) ? action.selectedTradeIds : [];
            // union
            return Array.from(new Set([...fromAllocations, ...fromSelection]));
        })();

        const lockedTradeIds = affectedTradeIds.filter(isLocked);
        if (lockedTradeIds.length > 0) {
            const labels = lockedTradeIds.map(id => (findTrade(id) as any)?.execId || id).join(', ');
            notifications.show({
                title: 'Trade locked',
                message: `No changes applied. Locked trade${lockedTradeIds.length > 1 ? 's' : ''}: ${labels}`,
                color: 'yellow',
                icon: <IconLock size="1rem" />,
            });
            return false;
        }

        const dateId = format(date, 'dd-MM-yyyy');
        const batch = writeBatch(db);
        const newAllocations = { ...allocations };

        let successSummary = '';
        let failureSummary = '';
        let historyDescription = '';

        switch (action.type) {
            case 'ADD_ALLOCATION': {
                const { tradeId, portfolioId, qty } = action;
                const currentAllocs = newAllocations[tradeId] || [];
                const idx = currentAllocs.findIndex(a => a.portfolioId === portfolioId);
                const updatedAllocs = idx >= 0
                    ? (() => { const a = [...currentAllocs]; a[idx].qty = stripNoise(a[idx].qty + qty); return a; })()
                    : [...currentAllocs, { portfolioId, qty }];

                newAllocations[tradeId] = updatedAllocs;
                batch.update(doc(db, 'trade_dates', dateId, 'trades', tradeId), { allocations: updatedAllocs });

                const t = findTrade(tradeId);
                const exec = (t as any)?.execId || tradeId;
                const pLabel = portfolioCodeMap?.[portfolioId]?.code ?? portfolioId;

                successSummary = `Assigned ${fmtQty(qty)} to ${pLabel} (${exec})`;
                historyDescription = `Assigned ${fmtQty(qty)} to ${pLabel}`;
                break;
            }

            case 'REMOVE_ALLOCATION': {
                const { tradeId, portfolioId, qty } = action;
                const currentAllocs = newAllocations[tradeId] || [];
                const idx = currentAllocs.findIndex(a => a.portfolioId === portfolioId);
                if (idx >= 0) {
                    const updatedAllocs = [...currentAllocs];
                    const newQty = stripNoise(updatedAllocs[idx].qty - qty);
                    if (newQty <= QTY_EPSILON) updatedAllocs.splice(idx, 1);
                    else updatedAllocs[idx] = { ...updatedAllocs[idx], qty: newQty };

                    newAllocations[tradeId] = updatedAllocs;
                    batch.update(doc(db, 'trade_dates', dateId, 'trades', tradeId), { allocations: updatedAllocs });
                    const t = findTrade(tradeId);
                    const exec = (t as any)?.execId || tradeId;
                    const pLabel = portfolioCodeMap?.[portfolioId]?.code ?? portfolioId;

                    successSummary = `Removed ${pLabel} (${fmtQty(qty)}) from ${exec}`;
                    historyDescription = `Removed ${pLabel} allocation from ${exec}`;
                } else {
                    failureSummary = `No allocation found for ${portfolioId} on trade ${tradeId}`;
                }


                break;
            }

            case 'BULK_ADD': {
                let total = 0; let tradesCount = 0; const seen = new Set<string>();
                for (const alloc of action.allocations) {
                    const cur = newAllocations[alloc.tradeId] || [];
                    const idx = cur.findIndex(a => a.portfolioId === alloc.portfolioId);
                    const upd = idx >= 0 ? (() => { const a = [...cur]; a[idx].qty = stripNoise(a[idx].qty + alloc.qty); return a; })()
                        : [...cur, { portfolioId: alloc.portfolioId, qty: alloc.qty }];
                    newAllocations[alloc.tradeId] = upd;
                    batch.update(doc(db, 'trade_dates', dateId, 'trades', alloc.tradeId), { allocations: upd });

                    total += alloc.qty;
                    if (!seen.has(alloc.tradeId)) { seen.add(alloc.tradeId); tradesCount++; }
                }
                successSummary = `Bulk assigned ${fmtQty(total)} across ${tradesCount} trade${tradesCount > 1 ? 's' : ''}`;
                historyDescription = successSummary;
                break;
            }

            case 'BULK_REMOVE': {
                let totalRemoved = 0; let tradesCount = 0; const seen = new Set<string>();
                for (const alloc of action.allocations) {
                    const cur = newAllocations[alloc.tradeId] || [];
                    const idx = cur.findIndex(a => a.portfolioId === alloc.portfolioId);
                    if (idx >= 0) {
                        const upd = [...cur];
                        const newQty = stripNoise(upd[idx].qty - alloc.qty);
                        if (newQty <= QTY_EPSILON) { totalRemoved += upd[idx].qty; upd.splice(idx, 1); }
                        else { upd[idx] = { ...upd[idx], qty: newQty }; totalRemoved += alloc.qty; }
                        newAllocations[alloc.tradeId] = upd;
                        batch.update(doc(db, 'trade_dates', dateId, 'trades', alloc.tradeId), { allocations: upd });
                        if (!seen.has(alloc.tradeId)) { seen.add(alloc.tradeId); tradesCount++; }
                    }
                }
                if (seen.size > 0) {
                    successSummary = `Deleted allocations (${fmtQty(totalRemoved)} total) from ${tradesCount} trade${tradesCount > 1 ? 's' : ''}`;
                    historyDescription = successSummary;
                } else {
                    failureSummary = 'No allocations were removed.';
                }

                break;
            }
        }

        await batch.commit();
        setAllocations(newAllocations);

        // partial input refresh (remaining per affected trade; keep a sensible portfolioId)
        const firstPortfolioByTrade: Record<string, string | undefined> = {};
        if (action.type === 'ADD_ALLOCATION' || action.type === 'REMOVE_ALLOCATION') {
            firstPortfolioByTrade[action.tradeId] = (action as any).portfolioId;
        } else {
            for (const a of (action as any).allocations) {
                if (!firstPortfolioByTrade[a.tradeId]) firstPortfolioByTrade[a.tradeId] = a.portfolioId;
            }
        }
        const updates: Record<string, { portfolioId: string; qty: number }> = {};
        for (const tid of affectedTradeIds) {
            const t = findTrade(tid); if (!t) continue;
            const totalAllocated = sumQty(newAllocations[tid] || []);
            const rawRemaining = stripNoise(((t as any)?.filledQty || 0) - totalAllocated);
            const remaining = rawRemaining < QTY_EPSILON ? 0 : rawRemaining;
            const pid = firstPortfolioByTrade[tid] ?? (newAllocations[tid]?.[0]?.portfolioId ?? '');
            if (pid) updates[tid] = { portfolioId: pid, qty: remaining };
        }
        if (Object.keys(updates).length) setPartialInput(prev => ({ ...prev, ...updates }));

        if (successSummary) {
            notifications.show({
                title: 'Allocation updated',
                message: successSummary,
                color: 'green',
                icon: <IconCheck size="1rem" />,
            });
        } else if (failureSummary) {
            notifications.show({
                title: 'No Allocation updated',
                message: failureSummary,
                color: 'red',
                icon: <IconX size="1rem" />,
            });
            return false;
        }

        // centralized history (skip during undo/redo)
        if (!isUndoRedoOperation.current && historyDescription) {
            pushActionRef.current?.(action, historyDescription, userContext.user?.uid);
        }
        return true;
    }, [date, allocations, trades, portfolioCodeMap, setPartialInput, userContext]);



    // ✅ Initialize undo/redo so it calls the same applyAllocationAction,
    //    but marks that we're in an undo/redo (to avoid re-pushing).
    const {
        pushAction, // we'll mirror this into the ref right after
        undo,
        redo,
        canUndo,
        canRedo,
        getHistory,
        clearHistory,
        getStats,
        undoLoading,
        redoLoading,
    } = useActionBasedUndoRedo({
        maxHistorySize: 75,
        maxHistoryAge: 240,
        onApplyAction: async (action) => {
            isUndoRedoOperation.current = true;
            try {
                // return the boolean so the hook knows whether to move the pointer
                return await applyAllocationAction(action);
            } finally {
                isUndoRedoOperation.current = false;
            }
        },
    });

    // mirror pushAction into a ref to break the circular dependency
    useEffect(() => {
        pushActionRef.current = pushAction;
    }, [pushAction]);




    // Modified handleAssign to track only the action
    // ✅ Callers (trimmed): no pushAction / no setPartialInput / no notifications

    const handleAssign = useCallback(
        async (trade: Trade & { customInput?: { portfolioId: string; qty: number } }, onLoadingChange: (b: boolean) => void) => {
            if (!date) return;
            const input = trade.customInput || partialInput[trade.id];
            if (!input?.portfolioId) return;

            const assigned = (allocations[trade.id] || []).reduce((s, a) => s + a.qty, 0);
            const rem = stripNoise(trade.filledQty - assigned);
            const qty = Math.min(input.qty, rem);
            if (qty <= QTY_EPSILON) return;

            onLoadingChange(true);
            try {
                await applyAllocationAction({ type: 'ADD_ALLOCATION', tradeId: trade.id, portfolioId: input.portfolioId, qty });
            } catch (e) {
                console.error('Error updating allocation:', e);
            } finally {
                onLoadingChange(false);
            }
        },
        [date, partialInput, allocations, applyAllocationAction]
    );

    const handleRemoveAlloc = useCallback(async (trade: Trade, pid: string, onLoadingChange: (b: boolean) => void) => {
        onLoadingChange(true);
        try {
            if (!date) return;
            const currentAlloc = allocations[trade.id]?.find(a => a.portfolioId === pid);
            if (!currentAlloc) return;
            await applyAllocationAction({ type: 'REMOVE_ALLOCATION', tradeId: trade.id, portfolioId: pid, qty: currentAlloc.qty });
        } catch (e) {
            console.error('Error removing allocation:', e);
        } finally {
            onLoadingChange(false);
        }
    }, [date, allocations, applyAllocationAction]);

    const handleAssignBulk = useCallback(
        async (tradeIds: string[], portfolioId: string, onLoadingChange: (b: boolean) => void) => {
            if (!date || tradeIds.length === 0) return;
            onLoadingChange(true);
            try {
                const allocationsToAdd: Array<{ tradeId: string; portfolioId: string; qty: number }> = [];
                for (const tradeId of tradeIds) {
                    const trade = trades.find(t => t.id === tradeId);
                    if (!trade) continue;
                    const remaining = getRemainingQty(trade, allocations[tradeId] || []);
                    if (remaining <= 0) continue;
                    allocationsToAdd.push({ tradeId, portfolioId, qty: remaining });
                }
                if (allocationsToAdd.length > 0) {
                    await applyAllocationAction({ type: 'BULK_ADD', allocations: allocationsToAdd });
                }
            } catch (e) {
                console.error('Error in bulk assignment:', e);
            } finally {
                onLoadingChange(false);
            }
        },
        [date, allocations, trades, getRemainingQty, applyAllocationAction]
    );

    const handleAssignBulkWithQty = useCallback(
        async (tradeIds: string[], portfolioId: string, qty: number, onLoadingChange: (b: boolean) => void) => {
            if (!date || tradeIds.length === 0) return;
            onLoadingChange(true);
            try {
                let remainingBulkQty = qty;
                const allocationsToAdd: Array<{ tradeId: string; portfolioId: string; qty: number }> = [];
                for (const tradeId of tradeIds) {
                    if (remainingBulkQty <= 0) break;
                    const trade = trades.find(t => t.id === tradeId);
                    if (!trade) continue;
                    const remaining = getRemainingQty(trade, allocations[tradeId] || []);
                    if (remaining <= 0) continue;
                    const qtyToAssign = Math.min(remaining, remainingBulkQty);
                    remainingBulkQty -= qtyToAssign;
                    allocationsToAdd.push({ tradeId, portfolioId, qty: qtyToAssign });
                }
                if (allocationsToAdd.length > 0) {
                    await applyAllocationAction({ type: 'BULK_ADD', allocations: allocationsToAdd });
                }
            } catch (e) {
                console.error('Error in bulk assignment with qty:', e);
            } finally {
                onLoadingChange(false);
            }
        },
        [date, allocations, trades, getRemainingQty, applyAllocationAction]
    );

    const handleAssignBulkWithAllocationMap = useCallback(
        async (tradesAllocation: Map<string, TradeAllocation[]>, onLoadingChange: (b: boolean) => void, updateLoading?: boolean) => {
            if (!date || tradesAllocation.size === 0) return;
            if (updateLoading != false) onLoadingChange(true);

            try {
                const allocationsToAdd: Array<{ tradeId: string; portfolioId: string; qty: number }> = [];

                const fractionalExchanges = configContext.tradesMap?.fractionalExchanges ?? [];
                for (const [tradeId, tradeAllocations] of tradesAllocation) {
                    const trade = trades.find(t => t.id === tradeId);
                    if (!trade) continue;
                    if (!isAllfundsTradeAllocatable(trade)) continue; // in-flight AllFunds rows can't be allocated
                    // Fractional quantities are only allowed on exchanges opted in via config
                    if (!fractionalExchanges.includes(trade.exchange) && tradeAllocations.some(a => !Number.isInteger(a.qty))) {
                        notifications.show({
                            title: 'Fractional quantity rejected',
                            message: `Trade ${trade.execId || tradeId}: exchange ${trade.exchange || '?'} allocates in whole units only`,
                            color: 'red',
                            icon: <IconX size="1rem" />
                        });
                        continue;
                    }

                    const currentAllocs = allocations[tradeId] || [];
                    const totalAllocated = currentAllocs.reduce((s, a) => s + a.qty, 0);
                    const tradeTotal = trade.filledQty || 0;
                    const remaining = tradeTotal - totalAllocated;
                    if (remaining <= 0) continue;

                    for (const { portfolioId, qty } of tradeAllocations) {
                        if (qty <= 0) continue;
                        const currentTotalWithNew = allocationsToAdd.filter(a => a.tradeId === tradeId).reduce((s, a) => s + a.qty, totalAllocated);
                        const currentRemaining = tradeTotal - currentTotalWithNew;
                        if (currentRemaining <= 0) continue;
                        if (currentRemaining < qty) {
                            notifications.show({
                                title: 'Failed to allocate',
                                message: `Not enough remaining quantity for trade ${tradeId}. Requested: ${qty}, Remaining: ${currentRemaining}`,
                                color: 'red',
                                icon: <IconX size="1rem" />
                            });
                            continue;
                        }
                        allocationsToAdd.push({ tradeId, portfolioId, qty });
                    }
                }

                if (allocationsToAdd.length > 0) {
                    await applyAllocationAction({ type: 'BULK_ADD', allocations: allocationsToAdd });
                }
            } catch (e) {
                console.error('Error in Excel allocation import:', e);
            } finally {
                if (updateLoading != false) onLoadingChange(false);
            }
        },
        [date, allocations, trades, applyAllocationAction, configContext]
    );

    const handleDeleteAllAllocations = useCallback(
        async (tradeIds: string[], onLoadingChange: (isLoading: boolean) => void) => {
            if (!date || tradeIds.length === 0) return;

            onLoadingChange(true);
            try {
                const allocationsToRemove: Array<{ tradeId: string; portfolioId: string; qty: number }> = [];

                for (const tradeId of tradeIds) {
                    const tradeAllocs = allocations[tradeId] || [];
                    for (const alloc of tradeAllocs) {
                        allocationsToRemove.push({ tradeId, portfolioId: alloc.portfolioId, qty: alloc.qty });
                    }
                }

                if (allocationsToRemove.length > 0) {
                    await applyAllocationAction({
                        type: 'BULK_REMOVE',
                        allocations: allocationsToRemove,
                        selectedTradeIds: tradeIds, // 👈 important
                    });
                } else {
                    // even if nothing to remove, we still want to block if any selected trade is locked
                    await applyAllocationAction({
                        type: 'BULK_REMOVE',
                        allocations: [],
                        selectedTradeIds: tradeIds, // 👈 will trigger the lock gate and just exit (with toast)
                    });
                }
            } catch (e) {
                console.error('Error deleting allocations:', e);
            } finally {
                onLoadingChange(false);
            }
        },
        [date, allocations, applyAllocationAction]
    );


    // Handle undo with confirmation
    const handleUndo = useCallback(async () => {
        const history = getHistory();
        const lastAction = history.find(h => h.isCurrent);

        if (lastAction && lastAction.ageInMinutes > 60) {
            const confirmed = await confirm({
                title: 'Undo Old Action',
                message: `This action "${lastAction.description}" is ${lastAction.ageDisplay} old. Are you sure you want to undo it?`,
                confirmText: 'Yes, Undo',
                cancelText: 'Cancel',
                type: 'warning',
            });

            if (!confirmed) return;
        }

        isUndoRedoOperation.current = true;

        try {
            await undo();
            // notifications.show({
            //     title: 'Action Undone',
            //     message: lastAction?.description || 'Last action undone',
            //     color: 'blue',
            // });
        } catch (error) {
            console.error('Error undoing action:', error);
            // notifications.show({
            //     title: 'Undo Failed',
            //     message: 'Failed to undo the action',
            //     color: 'red',
            // });
        }
        // finally {
        //     isUndoRedoOperation.current = false;
        // }
    }, [undo, getHistory, confirm]);

    // Handle redo
    const handleRedo = useCallback(async () => {
        isUndoRedoOperation.current = true;

        try {
            await redo();
            // notifications.show({
            //     title: 'Action Redone',
            //     message: 'Action has been redone',
            //     color: 'blue',
            // });
        } catch (error) {
            console.error('Error redoing action:', error);
            // notifications.show({
            //     title: 'Redo Failed',
            //     message: 'Failed to redo the action',
            //     color: 'red',
            // });
        }
        // finally {
        //     isUndoRedoOperation.current = false;
        // }
    }, [redo]);


    // Clear history when date changes
    useEffect(() => {
        clearHistory();
    }, [date, clearHistory]);
    const onPortfolioChange = useCallback((id: string, v: string) => {
        setPartialInput(prev => ({ ...prev, [id]: { ...prev[id], portfolioId: v } }));
    }, []);
    const onQtyChange = useCallback((id: string, v: number) => {
        setPartialInput(prev => ({ ...prev, [id]: { ...prev[id], qty: v } }));
    }, []);

    // Memoized options for the portfolio select dropdown
    const portfolioOptions = useMemo(
        () => {
            console.log('Creating portfolio options list');
            return allPortfolios.map(p => ({ value: p.id!, label: `${p.code} - ${p.name_en}` }));
        },
        [allPortfolios]
    );


    // Setup Mantine React Table with grouping by orderId
    const columns = useMemo<MRT_ColumnDef<EmbellishedTrade>[]>(() => [
        { accessorKey: 'orderId', header: 'Order ID', enableGrouping: true, size: 110 },
        { accessorKey: 'execId', header: 'Trade ID', size: 50 },
        { accessorKey: 'accountId', header: 'Account ID', size: 100, filterVariant: 'select' },
        {
            accessorKey: 'formattedTime',
            header: 'Time',
            size: 70
        },

        {
            accessorKey: 'side',
            header: 'Side',
            // filterVariant: 'select',

            aggregationFn: showValueIfSame,
            size: 75,
            AggregatedCell: ({ cell }: { cell: MRT_Cell<EmbellishedTrade, 'BUY' | 'SELL' | null> }) => {
                const side = cell.getValue();
                if (side === 'BUY') return <BUY_BADGE />;
                if (side === 'SELL') return <SELL_BADGE />;
                return <Text c="dimmed" fs="italic">Multiple</Text>;
            },
            Cell: ({ cell }) => {
                const side = cell.getValue<'BUY' | 'SELL'>();
                return side === 'BUY' ? <BUY_BADGE /> : <SELL_BADGE />;
            },
        },

        {
            accessorFn: row => {
                const info = symbolMap[row.firebaseKey ?? String(row.symbolCode)] || { ticker: String(row.symbol), name: '' };
                return info.ticker;
            },
            id: 'symbol',
            header: 'Symbol',
            size: 190,
            filterVariant: 'select',
            aggregationFn: showValueIfSame,
            AggregatedCell: ({ cell }: { cell: MRT_Cell<EmbellishedTrade, any | null> }) => {
                const ticker = cell.getValue();
                return ticker !== null
                    ? <Text fw={500}>{ticker}</Text>
                    : <Text c="dimmed" fs="italic">Multiple</Text>;
            },
            Cell: ({ row }) => {
                const symbolID = row.original.firebaseKey ?? String(row.original.symbolCode);
                const info = symbolMap[symbolID] || { ticker: row.original.symbol, name: '' };

                // Use our cached symbol component
                return <CachedSymbol symbolCode={row.original.symbol} info={info} />;
            },
        },
        {
            accessorKey: 'price',
            header: 'Price',
            size: 80,
            aggregationFn: showValueIfSame,
            AggregatedCell: ({ cell }: { cell: MRT_Cell<EmbellishedTrade, number | null> }) => {
                const price = cell.getValue();
                return price !== null
                    ? <Text fw={500}>{price.toFixed(2)}</Text>
                    : <Text c="dimmed" fs="italic">Multiple</Text>;
            },
            Cell: ({ row }) => row.original.formattedPrice,
        },
        {
            accessorKey: 'netSettle',
            header: 'Amount',
            size: 110,
            aggregationFn: 'sum',
            AggregatedCell: ({ cell }: { cell: MRT_Cell<EmbellishedTrade, number> }) => (
                <Box style={{ fontWeight: 'bold' }}>
                    {(cell.getValue<number>() ?? 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}
                </Box>
            ),
            // full settlement amount (incl. fees when the trade carries any)
            Cell: ({ row }) => `${(row.original.netSettle ?? 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}${row.original.currency ? ` ${row.original.currency}` : ''}`,
        },
        {
            accessorKey: 'filledQty',
            header: 'Qty (Alloc/Total)',
            size: 130,
            aggregationFn: 'sum',
            AggregatedCell: ({ cell, row }) => {
                // Get all leaf rows to calculate allocated amount
                let leafRows: any[] = [];
                try {
                    leafRows = row.getLeafRows ? row.getLeafRows() : [];
                } catch (error) {
                    return <Box style={{ fontWeight: 'bold' }}>
                        Total: {fmtQty(cell.getValue<number>())}
                    </Box>;
                }

                // Calculate total allocated across all trades in this group
                let totalAllocated = 0;
                leafRows.forEach(leafRow => {
                    // Check if the leaf row has an original trade with an ID and it not a group header
                    if (!leafRow.getIsGrouped() && leafRow.original?.id) {
                        const rowAllocations = allocations[leafRow.original.id] || [];
                        totalAllocated += rowAllocations.reduce((sum, a) => sum + a.qty, 0);
                    }
                });

                const totalQty = cell.getValue<number>() || 0;

                return (
                    <Box style={{ fontWeight: 'bold' }}>
                        {fmtQty(totalAllocated)}/{fmtQty(totalQty)}
                    </Box>
                );
            },
            Cell: ({ row }) => {
                const trade = row.original;
                const tradeAllocations = allocations[trade.id] || [];
                const totalAllocated = tradeAllocations.reduce((sum, a) => sum + a.qty, 0);
                const totalQty = trade.filledQty;

                return `${fmtQty(totalAllocated)}/${fmtQty(totalQty)}`;
            },
        },
        {
            accessorFn: row => allocations[row.id] || [],
            id: 'allocations',
            header: 'Allocations',
            size: 250,
            aggregationFn: 'count',
            // Filter trades by the portfolio they're allocated to (number/code or name)
            filterFn: (row, _columnId, filterValue) => {
                const term = String(filterValue ?? '').trim().toLowerCase();
                if (!term) return true;
                return (allocations[row.original.id] || []).some(a => {
                    const portfolio = portfolioCodeMap?.[a.portfolioId];
                    return a.portfolioId.toLowerCase().includes(term)
                        || (portfolio?.code ?? '').toLowerCase().includes(term)
                        || (portfolio?.name ?? '').toLowerCase().includes(term);
                });
            },
            AggregatedCell: ({ cell, row }) => {
                // Get all leaf rows to find allocations across grouped trades
                let leafRows: any[] = [];
                try {
                    leafRows = row.getLeafRows ? row.getLeafRows() : [];
                } catch (error) {
                    return <Box>{cell.getValue<number>()} Alloc(s)</Box>;
                }

                // Collect all allocations across grouped trades
                const allAllocations: { portfolioId: string; qty: number }[] = [];
                leafRows.forEach(leafRow => {
                    if (!leafRow.getIsGrouped() && leafRow.original?.id) {
                        const rowAllocations = allocations[leafRow.original.id] || [];
                        allAllocations.push(...rowAllocations);
                    }
                });

                // If no allocations found, show simple count
                if (allAllocations.length === 0) {
                    return <Box>No Allocations</Box>;
                }

                // Summarize allocations by portfolio
                const portfolioSummary = allAllocations.reduce((acc, alloc) => {
                    const portfolioId = alloc.portfolioId;
                    if (!acc[portfolioId]) {
                        acc[portfolioId] = 0;
                    }
                    acc[portfolioId] += alloc.qty;
                    return acc;
                }, {} as Record<string, number>);

                // Convert to array for display
                const summaryArray = Object.entries(portfolioSummary).map(([portfolioId, totalQty]) => {
                    const portfolio = portfolioCodeMap[portfolioId];
                    const portfolioDisplay = portfolio ? createPortfolioDisplayName(portfolio, 'first-word') : portfolioId;
                    const warnings = portfolioWarnings[portfolioId] || [];
                    return { portfolioId, portfolioDisplay, totalQty, warnings };
                });

                return (
                    <SimpleGrid cols={1} verticalSpacing="xs">
                        {summaryArray.map((summary, i) => (
                            <Box key={i}>
                                <Badge variant="light" color={summary.warnings.length > 0 ? "red" : "blue"}>
                                    {summary.portfolioDisplay}: {fmtQty(summary.totalQty)}
                                </Badge>
                                {summary.warnings.length > 0 && (
                                    <Tooltip
                                        label={
                                            <div>
                                                {summary.warnings.map((w, idx) => (
                                                    <div key={idx} style={{ marginBottom: 4 }}>
                                                        {w.message}
                                                    </div>
                                                ))}
                                            </div>
                                        }
                                        position="bottom"
                                        multiline
                                        maw={300}
                                    >
                                        <Badge color="red" variant="dot" size="xs" style={{ marginLeft: 4 }}>
                                            {summary.warnings.length} warning{summary.warnings.length !== 1 ? 's' : ''}
                                        </Badge>
                                    </Tooltip>
                                )}
                            </Box>
                        ))}
                    </SimpleGrid>
                );
            },
            Cell: ({ row }) => {
                const tradeAllocations = allocations[row.original.id] || [];
                if (tradeAllocations.length === 0) return null;

                return (
                    <SimpleGrid cols={1} verticalSpacing="xs">
                        {tradeAllocations.map((a, i) => {
                            // Find the portfolio to get its code
                            const portfolio = portfolioCodeMap[a.portfolioId];
                            let portfolioDisplay;

                            portfolioDisplay = portfolio
                                ? createPortfolioDisplayName(portfolio, 'first-word')
                                : a.portfolioId;



                            const warnings = portfolioWarnings[a.portfolioId] || [];
                            const hasWarnings = warnings.some(w => w.tradeId === row.original.id);


                            return (
                                <Box key={i}>
                                    <Tooltip openDelay={500} label={portfolio?.name_en ?? '-'}>
                                        <Badge
                                            variant="filled"
                                            color={hasWarnings ? "red" : "blue"}
                                            rightSection={readonly ? null :
                                                row.original.state == "LOCKED" || row.original.state === "GENEVA_ERROR" ?
                                                    <IconLock size={16} />
                                                    :
                                                    <RemoveAllocButton
                                                        trade={row.original}
                                                        portfolioId={a.portfolioId}
                                                        onRemove={handleRemoveAlloc}
                                                    />
                                            }
                                        >
                                            {portfolioDisplay}: {fmtQty(a.qty)}
                                        </Badge>
                                    </Tooltip>
                                    {hasWarnings && (
                                        <Tooltip
                                            label={
                                                <div>
                                                    {warnings
                                                        .filter(w => w.tradeId === row.original.id)
                                                        .map((w, idx) => (
                                                            <div key={idx} style={{ marginBottom: 4 }}>
                                                                {w.message}
                                                            </div>
                                                        ))}
                                                </div>
                                            }
                                            position="bottom"
                                            multiline
                                            maw={300}
                                        >
                                            <Badge color="red" variant="dot" size="xs" style={{ marginLeft: 4 }}>
                                                Warning
                                            </Badge>
                                        </Tooltip>
                                    )}
                                </Box>
                            );
                        })}
                    </SimpleGrid>
                );
            }
        },
        // only include this item if showSecret is true
        ...(!readonly
            ? [{
                id: 'action',
                header: 'Action',
                // enableColumnActions: false,
                size: 350,
                AggregatedCell: ({ cell }: { cell: MRT_Cell<EmbellishedTrade, unknown> }) => {
                    // Use snapshot balances for the first trade in this group
                    const firstRow = cell.row.getLeafRows ? cell.row.getLeafRows()[0].original : null;
                    const snapshotBalances = firstRow ? (runningSnapshots[firstRow.id.toString()] || runningBalances) : runningBalances;

                    //filter portfolio options based on selected asset manager
                    const filteredPortfolioOptions = portfolioOptions.filter(option => {
                        const portfolio = allPortfolios.find(p => p.id === option.value);
                        return matchesAssetManagerFilter(portfolio, selectedAssetManager);
                    });

                    return (
                        <AggregatedActionCell
                            row={cell.row}
                            allocations={allocations}
                            partialInput={partialInput}
                            portfolioOptions={filteredPortfolioOptions}
                            onPortfolioChange={onPortfolioChange}
                            handleAssignBulk={handleAssignBulk}
                            handleAssignBulkWithQty={handleAssignBulkWithQty}
                            handleDeleteAllAllocations={handleDeleteAllAllocations}
                            portfolioWarnings={portfolioWarnings}
                            isAllocationValid={isAllocationValid}
                            runningBalances={snapshotBalances}
                        />
                    );
                },
                Cell: ({ row }: { row: MRT_Row<EmbellishedTrade> }) => {
                    const rowAllocations = allocations[row.original.id] || [];
                    const remaining = getRemainingQty(row.original, rowAllocations);
                    if (remaining <= 0) return null;
                    const rowPartialInput = partialInput[row.original.id] || { portfolioId: '', qty: 0 };
                    // Use snapshot balances for this trade
                    const snapshotBalances = runningSnapshots[row.original.id.toString()] || runningBalances;

                    //filter portfolio options based on selected asset manager
                    const filteredPortfolioOptions = portfolioOptions.filter(option => {
                        const portfolio = allPortfolios.find(p => p.id === option.value);
                        return matchesAssetManagerFilter(portfolio, selectedAssetManager);
                    });

                    if (row.original?.state === "LOCKED") {
                        return (
                            <Text c="dimmed" >Trade is locked</Text>
                        );

                    } else if (row.original?.state === "GENEVA_ERROR") {
                        return (
                            <Text c="dimmed">Error sending to geneva</Text>
                        );
                    }


                    return (
                        <ActionCell
                            trade={row.original}
                            allocations={rowAllocations}
                            partialInput={rowPartialInput}
                            portfolioOptions={filteredPortfolioOptions}
                            onPortfolioChange={onPortfolioChange}
                            onQtyChange={onQtyChange}
                            handleAssign={handleAssign}
                            isAllocationValid={isAllocationValid}
                            portfolioWarnings={portfolioWarnings}
                            runningBalances={snapshotBalances}
                        />
                    );
                },
            }]
            : []),

        {
            accessorKey: 'dealer',
            header: 'Dealer',
            aggregationFn: showValueIfSame,
            size: 230,
            filterVariant: 'select',
            AggregatedCell: ({ cell }: { cell: MRT_Cell<Trade, string | null> }) => {
                const dealer = cell.getValue();
                return dealer !== null ? <Text>{dealer}</Text> : <Text c="dimmed" fs="italic">Multiple</Text>;
            },
        },
    ], [allocations, partialInput, symbolMap, allPortfolios, onPortfolioChange, onQtyChange, handleAssign, handleAssignBulk, handleRemoveAlloc, portfolioOptions, handleDeleteAllAllocations, portfolioCodeMap, portfolioWarnings, runningBalances, runningSnapshots, selectedAssetManager]);

    const table = useMantineReactTable({
        columns,
        data: trades,
        getRowId: row => row.id.toString(),
        enableGrouping: true,
        enableColumnDragging: false,
        initialState: { showColumnFilters: true, density: 'xs', pagination: { pageIndex: 0, pageSize: 50 }, columnPinning: { left: ['mrt-row-expand', 'mrt-row-select'], right: ['action'] } },
        onRowSelectionChange: setRowSelection,
        state: {
            grouping,
            sorting,
            columnFilters,
            columnSizing,
            columnVisibility,
            rowSelection,
            columnPinning: { left: ['mrt-row-expand', 'mrt-row-select'], right: ['action'] }
        },
        onGroupingChange: (updater) => {
            const newGrouping = typeof updater === 'function' ? updater(grouping) : updater;
            setGrouping(newGrouping);
            try {
                localStorage.setItem('tradesTableGrouping', JSON.stringify(newGrouping));
            } catch (error) {
                console.error("Error saving grouping state to localStorage", error);
            }
        },
        onSortingChange: (updater) => {
            const newSorting = typeof updater === 'function' ? updater(sorting) : updater;
            setSorting(newSorting);
            try {
                localStorage.setItem('tradesTableSorting', JSON.stringify(newSorting));
            } catch (error) {
                console.error("Error saving sorting state to localStorage", error);
            }
        },
        onColumnFiltersChange: (updater) => {
            const newFilters = typeof updater === 'function' ? updater(columnFilters) : updater;
            setColumnFilters(newFilters);
            try {
                if (!readonly) {
                    localStorage.setItem('tradesTableFilters', JSON.stringify(newFilters));
                }

            } catch (error) {
                console.error("Error saving filters state to localStorage", error);
            }
        },
        onColumnSizingChange: (updater) => {
            const newSizing = typeof updater === 'function' ? updater(columnSizing) : updater;
            setColumnSizing(newSizing);

            //let's print it out
            console.log("newSizing", newSizing);
            try {
                localStorage.setItem('tradesTableColumnSizing', JSON.stringify(newSizing));
            } catch (error) {
                console.error("Error saving column sizing state to localStorage", error);
            }
        },
        onColumnVisibilityChange: (updater) => {
            const newVisibility = typeof updater === 'function' ? updater(columnVisibility) : updater;
            setColumnVisibility(newVisibility);
            try {
                localStorage.setItem('tradesTableColumnVisibility', JSON.stringify(newVisibility));
            } catch (error) {
                console.error("Error saving column visibility state to localStorage", error);
            }
        },
        // added ToolbarAlertBanner to handle action for selected rows
        renderToolbarAlertBannerContent: ({ table }) => {
            const selectedRows = table.getSelectedRowModel().rows;
            const selectedCount = selectedRows.length;

            // Only show when rows are selected
            if (selectedCount === 0) return null;

            //filter portfolio options based on selected asset manager
            const filteredPortfolioOptions = portfolioOptions.filter(option => {
                const portfolio = allPortfolios.find(p => p.id === option.value);
                return matchesAssetManagerFilter(portfolio, selectedAssetManager);
            });

            // show snapshot balances for the first selected row
            const snapshotBalances = runningSnapshots[selectedRows[0].original.id.toString()] || runningBalances;

            return (
                <Box p="md" style={{ borderBottom: '1px solid #e0e0e0', backgroundColor: '#f8f9fa' }}>
                    <Group justify="start" align="center">
                        <Text size="sm" fw={500}>
                            Action on {selectedCount} row{selectedCount !== 1 ? 's' : ''} selected
                        </Text>

                        <AggregatedActionCell
                            row={undefined} // No row context for aggregated cell as we are using selectedRows
                            selectedRows={selectedRows}
                            allocations={allocations}
                            partialInput={partialInput}
                            portfolioOptions={filteredPortfolioOptions}
                            onPortfolioChange={onPortfolioChange}
                            handleAssignBulk={handleAssignBulk}
                            handleAssignBulkWithQty={handleAssignBulkWithQty}
                            handleDeleteAllAllocations={handleDeleteAllAllocations}
                            portfolioWarnings={portfolioWarnings}
                            isAllocationValid={isAllocationValid}
                            runningBalances={snapshotBalances}
                        />
                    </Group>
                </Box>
            );
        },

        renderTopToolbarCustomActions: ({ table }) => (
            <Box style={{ display: 'flex', gap: '16px', padding: '4px' }}>
                <DatePickerInput disabled={readonly} value={date} onChange={(value) => setDate(fromMantineDate(value))} excludeDate={value => {
                    const d = fromMantineDate(value);
                    return !d || !availableDateStrings.has(d.toDateString());
                }} clearable={false} placeholder="Select date" />
                {/* Added a Select to choose an asset manager's portfolio; defaults to the current user if available, otherwise "All". */}
                {!readonly && <>
                    <Select
                        data={allAssetManagers}
                        placeholder="Asset Manager"
                        value={selectedAssetManager}
                        onChange={val => setSelectedAssetManager(val || '')}
                        style={{ width: 300 }}
                        comboboxProps={{ width: 300, position: 'bottom-start' }}
                        limit={10}
                    />
                    <PortfolioAllocationStatsModal trades={trades} allocations={allocations} portfolios={allPortfolios.filter(portfolio => {
                        // const portfolio = allPortfolios.find(p => p.id === option.value);
                        return matchesAssetManagerFilter(portfolio, selectedAssetManager);
                    })} />
                    <WarningsModal uniqueWarnings={uniqueWarnings} />
                    <PortfolioGroupsManager portfolios={allPortfolios} assetManager={selectedAssetManager} onGroupsChange={(groups: CombinedPortfolioGroup[]) => {
                        console.log("Portfolio groups updated", groups);
                        setCombinedPortfolioGroup(groups);
                    }} />
                    <Menu shadow="md"  >
                        <Menu.Target>
                            <Tooltip label="Upload / Download Excel" withArrow>
                                <ActionIcon
                                    loading={excelAllocationLoading}
                                    size="lg"
                                    variant="light"
                                >
                                    <IconTable size={16} />
                                </ActionIcon>
                            </Tooltip>
                        </Menu.Target>

                        <Menu.Dropdown>
                            <Menu.Label>Upload</Menu.Label>
                            <Menu.Item
                                leftSection={<IconUpload size="1rem" />}
                                onClick={() => setExcelAllocationModalOpened(true)}>
                                Upload Allocations
                            </Menu.Item>
                            {canSeeGlobalDpm && (
                                <>
                                    <Menu.Item
                                        leftSection={<IconUpload size="1rem" />}
                                        onClick={() => setGlobalDpmUploadOpened(true)}>
                                        Upload Global DPM Allocations
                                    </Menu.Item>
                                    <Menu.Item
                                        leftSection={<IconTable size="1rem" />}
                                        onClick={() => setGlobalDpmUploadsListOpened(true)}>
                                        Global DPM Uploads
                                    </Menu.Item>
                                </>
                            )}
                            <Menu.Label>Download</Menu.Label>
                            <TradeAllocationExcel trades={selectedTrades} portfolios={filteredPortfolios} asMenu={true} />
                            <TradeAllocationExcel trades={selectedTrades} portfolios={filteredPortfolios} asMenu={true} hideAvailableShares={true} />
                        </Menu.Dropdown>
                    </Menu>

                    {/* <ActionIcon
                        size={"lg"}
                        m={1}
                        onClick={() => setAddModalOpened(true)}
                    >
                        <IconPlus size={16} />
                    </ActionIcon> */}
                    <Tooltip label="Show Manual Trades" withArrow>
                        <ActionIcon
                            size='lg'
                            variant='light'
                            onClick={() => setTableToShow('manualTrades')}
                        >
                            <IconArrowsLeftRight size={16} />
                        </ActionIcon>
                    </Tooltip>
                    <ExcelUploadComponent
                        onAllocationsUpdate={async (
                            excelAllocations: Map<string, TradeAllocation[]>
                        ): Promise<PortfolioWarning[]> => {
                            setExcelAllocationLoading(true);
                            const warnings: PortfolioWarning[] = [];
                            try {
                                // 1) still perform the assign work
                                await handleAssignBulkWithAllocationMap(excelAllocations, setExcelAllocationLoading, false);

                                // 2) wait based on config and fallback to 5s
                                await new Promise((r) => setTimeout(r, configContext.tradesMap?.excel_upload_waiting_time_ms ?? UNDO_WAIT_MS));

                                // 3) collect tradeIds from the Excel map keys
                                const tradeIdsFromExcel = new Set<string>(Array.from(excelAllocations?.keys?.() ?? []));

                                console.log("Checking portfolio warnings for trades from Excel:", tradeIdsFromExcel);

                                // 4) scan all portfolio warnings and collect matches

                                const allPortfolioWarnings: Record<string, PortfolioWarning[]> = portfolioWarningsRef.current ?? {};
                                console.log("All portfolio warnings before undo:", allPortfolioWarnings);
                                // console.log("YRH All portfolio uniqueWarnings before undo:", uniqueWarnings);
                                for (const [portfolioId, warns] of Object.entries(allPortfolioWarnings)) {
                                    for (const w of warns ?? []) {
                                        console.log(`Checking warning for Portfolio ${portfolioId}, Trade ${w.tradeId}`);
                                        if (tradeIdsFromExcel.has(w.tradeId)) {
                                            warnings.push(w);
                                        }
                                    }
                                }

                                // 5) undo only if needed (i.e., if any warnings were found)
                                if (warnings.length > 0) {
                                    try {
                                        console.log("Undoing allocations due to warnings:", warnings);
                                        await handleUndo();
                                    } catch {
                                        // ignore undo errors so we can still return the warnings
                                    }
                                }



                            } finally {
                                setExcelAllocationLoading(false);
                                console.log("Excel allocations updated", excelAllocations);
                            }
                            // 6) return the sentences (or [] if none)
                            return warnings;
                        }}
                        loading={excelAllocationLoading}
                        opened={excelAllocationModalOpened}
                        setOpened={setExcelAllocationModalOpened}
                    />


                    <UndoRedoControls
                        canUndo={canUndo}
                        canRedo={canRedo}
                        onUndo={handleUndo}
                        onRedo={handleRedo}
                        getHistory={getHistory}
                        clearHistory={clearHistory}
                        undoLoading={undoLoading}
                        redoLoading={redoLoading}
                        disabled={loading !== null || portfolioLoading !== null || undoLoading || redoLoading}
                    />
                </>}
            </Box>


        ),


        // mantineTableContainerProps: { style: { maxHeight: '100%' } },
        enableBottomToolbar: false,
        enableColumnResizing: true,
        enableGlobalFilterModes: !readonly,
        enablePagination: false,
        enableColumnPinning: true,
        enableRowVirtualization: true,
        enableStickyHeader: true,
        mantineTableContainerProps: {
            style: {

                maxHeight: 'calc(100dvh -  var(--app-shell-header-height) - 7.5rem)',
                overflowY: 'auto',          // keeps the scroll inside the table
            },
        },
        rowVirtualizerOptions: { overscan: 5 }, //optionally customize the row virtualizer
        // columnVirtualizerOptions: { overscan: 2 }, //optionally customize the column virtualizer
        enableFacetedValues: true,
        enableDensityToggle: false, //density does not work with memoized rows
        enableRowSelection: true,
        // enableHiding: false, //column hiding does not work with memoized rows
        // memoMode: 'rows', // memoize table rows to improve render performance, but break a lot of features


        mantinePaperProps: { shadow: 'none', withBorder: false, p: 0 }, // Thinner borders
        mantineTableProps: {
            // striped: true,
            highlightOnHover: true,
            // highlightOnHoverColor: 'yellow',
            // style: {
            //   fontSize: '12px', // Smaller font size
            // },
        },
        mantineTableBodyCellProps: {
            style: {
                padding: '2px 4px', // Reduced cell padding
            },
        },
        mantineTableHeadCellProps: {
            style: {
                padding: '4px 8px', // Reduced header padding
                fontWeight: 'bold',
            }
        },
        // Add row styling based on allocation status
        mantineTableBodyRowProps: ({ row }) => {
            const { totalAllocated, totalFilledQty } = calculateAllocationTotals(row, allocations);
            const isGrouped = row.getIsGrouped();
            const isFullyAlloc = isFullyAllocated(totalAllocated, totalFilledQty);

            let backgroundColor;
            if (isGrouped && isFullyAlloc) {
                backgroundColor = 'rgba(14, 109, 41, 0.15)'; // Darker green for fully allocated groups
            } else if (isGrouped) {
                backgroundColor = 'rgba(99, 102, 241, 0.1)'; // Light indigo for groups
            } else if (isFullyAlloc) {
                backgroundColor = 'rgba(0, 180, 0, 0.1)'; // Green for fully allocated rows
            }

            return {
                onClick: row.getCanExpand() ? (event) => {
                    // Don't expand row if clicking on interactive elements
                    const target = event.target as HTMLElement;
                    const isInteractiveElement = target.closest('button, select, input, [role="combobox"], [role="button"]');

                    if (!isInteractiveElement) {
                        row.getToggleExpandedHandler()();
                    }
                } : undefined,
                sx: { cursor: row.getCanExpand() ? 'pointer' : 'default' },
                ...(backgroundColor && { style: { backgroundColor } })
            };
        },
    });

    // Effect to save side filter to localStorage
    useEffect(() => {
        try {
            localStorage.setItem('tradesTableSideFilter', filterSide);
        } catch (e) {
            console.error("Error saving side filter to localStorage", e);
        }
    }, [filterSide]);

    // Validate if allocation is valid (has enough securities to sell or cash to buy)
    const isAllocationValid = useCallback((
        portfolioId: string,
        symbolID: string,
        side: 'BUY' | 'SELL',
        quantity: number,
        price: number,
        exchange: string,
        currency: string | undefined,
        specificBalances?: Record<string, RunningBalance>
    ): { valid: boolean; message: string } => {
        // Use the specificBalances if provided, otherwise use the current runningBalances
        const balancesSource = specificBalances || runningBalances;
        const balance = balancesSource[portfolioId];
        if (!balance) {
            return { valid: false, message: 'Portfolio not found' };
        }

        if (side === 'SELL') {
            // Check if portfolio has enough securities to sell
            const security = balance.securities[symbolID];
            if (!security) {
                return { valid: false, message: `No ${symbolID} positions in portfolio` };
            }

            if (security.quantity < quantity) {
                return {
                    valid: false,
                    message: `Insufficient ${symbolID}: ${security.quantity.toLocaleString()} available, ${quantity.toLocaleString()} needed`
                };
            }
        } else { // BUY
            // Check if portfolio has enough cash to buy.
            // For KWD trades, price is in fils and cash is in KWD, so convert.
            // For other currencies, price and cash share the same native currency.
            // Cash is keyed by currency (not exchange), so AED trades on ADX and DFM
            // draw from the same AED cash bucket.
            const costAmount = getTradeCashAmount(currency, price, quantity);
            const currencyKey = resolveCurrency(currency);
            const availableCash = balance.cashMap ? balance.cashMap[currencyKey] || 0 : 0;
            if (!balance.cashMap || availableCash < costAmount) {
                return {
                    valid: false,
                    message: `Insufficient ${currencyKey} cash: ${availableCash.toLocaleString(undefined, { minimumFractionDigits: 3, maximumFractionDigits: 3 })} available, ${costAmount.toLocaleString(undefined, { minimumFractionDigits: 3, maximumFractionDigits: 3 })} needed`
                };
            }
        }

        return { valid: true, message: '' };
    }, [runningBalances]);

    // Add a function to calculate running balances in trade execution order
    const calculateSequentialBalances = useCallback((
        initialBalances: Record<string, RunningBalance>,
        tradesList: Trade[],
        allocationMap: Record<string, { portfolioId: string; qty: number }[]>
    ) => {
        // Sort trades by execution time (tradeDate)
        const sortedTrades = [...tradesList].sort((a, b) =>
            a.tradeDate.seconds - b.tradeDate.seconds
        );

        // Clone the initial balances
        const runningBalances: Record<string, RunningBalance> = JSON.parse(JSON.stringify(initialBalances));
        // Initialize snapshots for each trade
        const snapshots: Record<string, Record<string, RunningBalance>> = {};

        // Take a snapshot of initial balances (before any trades)
        const initialSnapshot = JSON.parse(JSON.stringify(runningBalances));

        // Track which trades cause issues
        const warningsByPortfolio: Record<string, PortfolioWarning[]> = {};

        // Process each trade in chronological order
        sortedTrades.forEach(trade => {
            const tradeAllocs = allocationMap[trade.id] || [];

            tradeAllocs.forEach(alloc => {
                const { portfolioId, qty } = alloc;

                const portfolio = runningBalances[portfolioId] ?? { portfolioId, cash: 0, securities: {} };
                const symbolID = trade.firebaseKey ?? String(trade.symbolCode);
                // Cash is bucketed by currency (not exchange) so portfolios share a single
                // cash pool per currency across all exchanges that trade in that currency.
                const currencyKey = resolveCurrency(trade.currency);
                if (!portfolio.cashMap) {
                    portfolio.cashMap = {};
                }
                if (portfolio.cashMap[currencyKey] === undefined) {
                    portfolio.cashMap[currencyKey] = 0;
                }
                // Skip if portfolio doesn't exist in our tracking
                if (!runningBalances[portfolioId]) {
                    warningsByPortfolio[portfolioId] = warningsByPortfolio[portfolioId] || [];
                    warningsByPortfolio[portfolioId].push({
                        accountId: trade.accountId,
                        tradeId: trade.id,
                        message: `Portfolio ${portfolioId} not found`,
                        timestamp: trade.tradeDate.seconds,
                        side: trade.side,
                        symbolID: symbolID,
                        portfolioId: portfolioId
                    });
                    return;
                }


                if (trade.side === 'BUY') {
                    // Check if they have enough cash.
                    // Only KWD trades are priced in fils; others use their native currency.
                    const cost = getTradeCashAmount(trade.currency, trade.price, qty);
                    if (portfolio.cashMap[currencyKey] < cost) {
                        const cash = portfolio.cashMap[currencyKey] || 0;
                        // Record warning about negative cash
                        warningsByPortfolio[portfolioId] = warningsByPortfolio[portfolioId] || [];
                        warningsByPortfolio[portfolioId].push({
                            accountId: trade.accountId,
                            tradeId: trade.id,
                            message: `Insufficient ${currencyKey} cash: ${cash.toFixed(2)} available, ${cost.toFixed(2)} needed for ${symbolID}`,
                            timestamp: trade.tradeDate.seconds,
                            side: trade.side,
                            symbolID,
                            portfolioId: portfolioId
                        });
                    }

                    // Update cash balance (even if negative)
                    portfolio.cashMap[currencyKey] -= cost;

                    // Update securities position
                    if (!portfolio.securities[symbolID]) {
                        portfolio.securities[symbolID] = {
                            id: symbolID,
                            quantity: 0,
                            name: symbolMap[symbolID]?.name || symbolID
                        };
                    }

                    portfolio.securities[symbolID].quantity += qty;
                } else { // SELL
                    // Check if they have enough of the security
                    const security = portfolio.securities[symbolID];
                    if (!security) {
                        warningsByPortfolio[portfolioId] = warningsByPortfolio[portfolioId] || [];
                        warningsByPortfolio[portfolioId].push({
                            accountId: trade.accountId,
                            tradeId: trade.id,
                            message: `No ${symbolID} positions in portfolio`,
                            timestamp: trade.tradeDate.seconds,
                            side: trade.side,
                            symbolID,
                            portfolioId: portfolioId
                        });

                        // Create a placeholder security to prevent null reference errors
                        portfolio.securities[symbolID] = {
                            id: symbolID,
                            quantity: 0,
                            name: symbolMap[symbolID]?.name || symbolID
                        };
                    }

                    // Now we can safely access the security
                    if (portfolio.securities[symbolID].quantity < qty) {
                        warningsByPortfolio[portfolioId] = warningsByPortfolio[portfolioId] || [];
                        warningsByPortfolio[portfolioId].push({
                            accountId: trade.accountId,
                            tradeId: trade.id,
                            message: `Overselling ${symbolID}: ${portfolio.securities[symbolID].quantity} available, ${qty} needed`,
                            timestamp: trade.tradeDate.seconds,
                            side: trade.side,
                            symbolID,
                            portfolioId: portfolioId
                        });
                    }

                    // Update position (even if negative)
                    portfolio.securities[symbolID].quantity -= qty;
                    if (portfolio.securities[symbolID].quantity <= 0) {
                        delete portfolio.securities[symbolID];
                    }

                    // Update cash.
                    // Only KWD trades are priced in fils; others use their native currency.
                    const value = getTradeCashAmount(trade.currency, trade.price, qty);
                    portfolio.cashMap[currencyKey] += value;
                }
            });

            // Store the current balances as the snapshot for this trade
            // This represents the state AFTER this trade's impact
            snapshots[trade.id.toString()] = JSON.parse(JSON.stringify(runningBalances));
        });

        return { runningBalances, warningsByPortfolio, snapshots };
    }, [symbolMap]);

    // Add a useEffect to calculate sequential balances when needed
    useEffect(() => {
        if (allPortfolios.length === 0 || trades.length === 0) return;

        // Get the initial balances from portfolios
        const initialBalances: Record<string, RunningBalance> = {};

        allPortfolios.forEach(portfolio => {
            if (!portfolio.id) return;

            const securities: Record<string, SecurityBalance> = {};
            const cashMap: Record<string, number> = {};

            // Initialize securities from local_position
            portfolio.position?.local_position?.forEach(position => {
                if (position.firebase_key && position.code && position.code !== 'KWD' && !position.cash) {
                    securities[position.firebase_key] = {
                        id: position.firebase_key,
                        quantity: position.quantity || 0,
                        name: position.name || ''
                    };
                } else if (position.code === 'KWD' && position.cash) {
                    const currencyKey = resolveCurrency(position.currency || position.code);
                    cashMap[currencyKey] = (cashMap[currencyKey] || 0) + (position.quantity || 0);
                }
            });

            // Add foreign positions if needed
            portfolio.position?.foreign_position?.forEach(position => {
                if (position.firebase_key && position.code && !position.cash) {
                    securities[position.firebase_key] = {
                        id: position.firebase_key,
                        quantity: position.quantity || 0,
                        name: position.name || ''
                    };
                } else if (position.cash && (position.currency || position.code)) {
                    const currencyKey = resolveCurrency(position.currency || position.code);
                    cashMap[currencyKey] = (cashMap[currencyKey] || 0) + (position.quantity || 0);
                }
            });

            initialBalances[portfolio.id] = {
                portfolioId: portfolio.id,
                cashMap,
                securities
            };
        });

        // Calculate sequential balances and snapshots
        const { runningBalances: sequential, warningsByPortfolio, snapshots } = calculateSequentialBalances(
            initialBalances,
            trades,
            allocations
        );

        // Update running balances state
        setRunningBalances(sequential);
        // Update per-trade snapshots
        setRunningSnapshots(snapshots);

        // Update portfolio warnings state
        setPortfolioWarnings(warningsByPortfolio);

        // Log warnings for debugging
        if (Object.keys(warningsByPortfolio).length > 0) {
            console.warn('Trade sequence warnings found:', warningsByPortfolio);
        }
    }, [allPortfolios, trades, allocations, calculateSequentialBalances]);

    const canSeeGlobalDpm = !!(userClaims.admin || userClaims.oper || userContext.availableTradeAccounts?.includes('GLOBAL_DPM'));

    // Debugging information removed to prevent excessive logging in production.
    if (loading || portfolioLoading) {
        return (
            <Center mt="xl" style={{ flexDirection: 'column', gap: 16 }}>
                <Loader size="lg" color="grape" />
                <Text size="lg" fw={500} c="grape">
                    {loading || portfolioLoading}
                </Text>
            </Center>
        );
    }

    return (
        <div style={{ padding: 0, }}>
            {/* Portfolio balances */}
            {/* <Card shadow="sm" padding="lg" style={{ marginBottom: 16 }}>
        <Text fw={500} style={{ marginBottom: 8 }}>Portfolio Balances</Text>
        <Group>
          {portfolioBalances.map(p => (
            <Badge key={p.id} color={p.balance < 0 ? 'red' : 'blue'}>
              {p.name}: {p.balance.toFixed(2)}
            </Badge>
          ))}
        </Group>
      </Card> */}



            {/* <Card shadow="sm" padding="lg" style={{ flex: 1, overflowY: 'auto', marginBottom: 16 }}> */}
            {/* Use an Accordion that, in read-only mode, is collapsible (default collapsed); in editable mode, remains always expanded and cannot be closed. */}
            {/* 
            <Accordion variant='contained' transitionDuration={1000} value={tradesAcordion} onChange={readonly ? setTradesAcordion : undefined}>
                <Accordion.Item key="trades" value="trades" >
                    <Accordion.Control disabled={trades.length <= 0}>
                        <Group p="apart">
                            <ThemeIcon
                                variant="gradient"
                                size="xl"
                                aria-label="Gradient action icon"
                                gradient={{ from: 'red', to: 'grape', deg: 180 }}
                            >
                                <IconTable size={30} />
                            </ThemeIcon>
                            <Text
                                component="span"
                                ta="center"
                                variant="gradient"
                                gradient={{ from: 'red', to: 'grape', deg: 180 }}
                                size="xl"
                                fw={700}
                            >
                                Trades
                            </Text>
                        </Group>

                    </Accordion.Control>
                    <Accordion.Panel>
                        <MantineReactTable table={table} />
                    </Accordion.Panel>
                </Accordion.Item>
            </Accordion> */}

            {tableToShow === 'normalTrades' && !readonly && canSeeGlobalDpm && (
                <GlobalDpmDateCards
                    selectedDateId={date ? format(date, 'dd-MM-yyyy') : undefined}
                    onSelectDate={setDate}
                />
            )}
            {tableToShow === 'normalTrades' && <MantineReactTable table={table} />}
            {tableToShow === 'normalTrades' && canSeeGlobalDpm && <GlobalDpmAllocationRowsPanel date={date} />}
            {canSeeGlobalDpm && (
                <>
                    <GlobalDpmAllocationUploadModal
                        opened={globalDpmUploadOpened}
                        setOpened={setGlobalDpmUploadOpened}
                    />
                    <GlobalDpmUploadsModal
                        opened={globalDpmUploadsListOpened}
                        setOpened={setGlobalDpmUploadsListOpened}
                    />
                </>
            )}
            {tableToShow === 'manualTrades' && <ManualTradesTable date={date ?? undefined} toggleView={() => setTableToShow('normalTrades')} />}

            {/* </Card> */}

            {/* <Divider style={{ marginBottom: 16 }} />

            {readonly && <SimpleGrid cols={4} spacing="lg">
                {uniqueWarnings && uniqueWarnings.length > 0
                    ? uniqueWarnings.map(group => (
                        <Card key={group.portfolioId} shadow="sm" padding="md" radius="md"
                            style={{
                                backgroundColor: '#fff5f5', // light red

                            }}
                        >
                            <Title order={5} mb="sm">
                                {group.portfolioId}
                            </Title>
                            <ScrollArea h={150}>
                                <Table verticalSpacing="xs">
                                    <Table.Thead>
                                        <Table.Tr>
                                            <Table.Th>Warnings</Table.Th>

                                        </Table.Tr>
                                    </Table.Thead>
                                    <Table.Tbody>
                                        {group.messages.map((msg, index) => (
                                            <Table.Tr key={`${group.portfolioId}-${index}`}>
                                                <Table.Td>{msg}</Table.Td>
                                            </Table.Tr>
                                        ))}
                                    </Table.Tbody>
                                </Table>
                            </ScrollArea>
                        </Card>
                    ))
                    : <Text> No warnings to display.</Text>
                }
            </SimpleGrid>} */}

            {/* <Button fullWidth
        // disabled={
        //   trades.some(t => (allocations[t.id] || []).reduce((s, a) => s + a.qty, 0) !== t.filledQty) ||
        //   portfolioBalances.some(p => p.balance < 0)
        // }
        onClick={async () => {

          // window.api.sendTrades(payload);
          // --- COM smoke test ---
          // const comStart = perf.start('COM test operation');

          // const result = await window.electron.runMoxyTest();
          // if (result.success) {
          //   console.log('⚡ COM test passed:', result.result);
          // } else {
          //   console.error('🔥 COM test failed:', result.error);
          // }
          console.log('Trades to  be send:', trades);
        }}>
        Send to Moxi
      </Button> */}
        </div>
    );
}
